// Finite scalar contract for conversation runtime diagnostics.
// UUID shape is a backstop. Runtime IDs are local; session/room joins come only from server-minted credentials.
export type RuntimeAttributeRule =
  | { kind: 'enum'; values: readonly string[] }
  | { kind: 'integer' | 'number' | 'boolean' | 'uuid' | 'version' | 'build' | 'schema' };

export const RUNTIME_ATTRIBUTE_RULES = {
  transition: {
    kind: 'enum',
    values: [
      'initialize',
      'initialize_web',
      'save_app_setup',
      'save_conversation_mode',
      'record_stop',
      'acknowledge_end',
      'resolve_feedback',
      'confirm_plan',
      'confirm_reminder',
      'complete',
    ],
  },
  host_instance_id: { kind: 'uuid' },
  session_generation: { kind: 'integer' },
  duplicate_count: { kind: 'integer' },
  exit_source: {
    kind: 'enum',
    values: [
      'member_end',
      'finish',
      'close',
      'android_back',
      'agent',
      'provider_failure',
      'unmount',
    ],
  },
  input_mode: { kind: 'enum', values: ['voice', 'keyboard'] },
  // Voice connect-failure classification. The two reason lists mirror the
  // shared VOICE_CONNECT_FAILURE_REASONS / VOICE_DISCONNECT_REASONS enums
  // (LiveKit ConnectionErrorReason and DisconnectReason names); a mobile test
  // pins them together. `connectFailureStatus` is the HTTP status LiveKit
  // reported, when it reported one.
  connectFailureReason: {
    kind: 'enum',
    values: [
      'not_allowed',
      'server_unreachable',
      'internal_error',
      'cancelled',
      'leave_request',
      'timeout',
      'websocket',
      'audio_session',
      'startup_timeout',
      'greeting_timeout',
      'unknown',
    ],
  },
  disconnectReason: {
    kind: 'enum',
    values: [
      'unknown_reason',
      'client_initiated',
      'duplicate_identity',
      'server_shutdown',
      'participant_removed',
      'room_deleted',
      'state_mismatch',
      'join_failure',
      'migration',
      'signal_close',
      'room_closed',
      'user_unavailable',
      'user_rejected',
      'sip_trunk_failure',
      'connection_timeout',
      'media_failure',
      'agent_error',
    ],
  },
  connectFailureStatus: { kind: 'integer' },
  // Voice SDK failure diagnosis, derived in memory from the error that failed
  // a voice connect: the setup step it failed in, its bounded class name, its
  // React Native rejection code, and for an audio-session failure the decoded
  // AVAudioSession error. The mobile diagnosis module owns these lists; a
  // mobile test pins them together. They also drive Error Tracking grouping.
  sdkSetupStage: {
    kind: 'enum',
    values: [
      'audio_pin',
      'audio_activate',
      'room_create',
      'signal',
      'negotiate',
      'publish',
      'superseded',
      'unknown',
    ],
  },
  sdkErrorClass: {
    kind: 'enum',
    values: [
      'Error',
      'TypeError',
      'RangeError',
      'AbortError',
      'AggregateError',
      'ConnectionError',
      'SessionConnectionError',
      'SignalRequestError',
      'UnsupportedServer',
      'NegotiationError',
      'UnexpectedConnectionState',
      'PublishTrackError',
      'TrackInvalidError',
      'DeviceUnsupportedError',
      'NotAllowedError',
      'NotFoundError',
      'NotReadableError',
      'OverconstrainedError',
      'other',
      'none',
    ],
  },
  sdkNativeCode: {
    kind: 'enum',
    values: ['startAudioSession', 'stopAudioSession', 'setAppleAudioConfiguration', 'other'],
  },
  audioSessionError: {
    kind: 'enum',
    values: [
      'insufficient_priority',
      'cannot_interrupt_others',
      'is_busy',
      'incompatible_category',
      'cannot_start_playing',
      'cannot_start_recording',
      'resource_not_available',
      'expired_session',
      'session_not_active',
      'siri_is_recording',
      'missing_entitlement',
      'media_services_failed',
      'unspecified',
      'bad_param',
      'other',
    ],
  },
  // How the best-effort iOS voice-processing audio-session pin failed.
  audioPinOutcome: { kind: 'enum', values: ['rejected', 'timed_out'] },
  failureClass: {
    kind: 'enum',
    values: [
      'authentication',
      'timeout',
      'network',
      'transport',
      'provider',
      'audio_session',
      'unknown',
    ],
  },
  // Chat-surface input state after a member-driven change: which control
  // changed it, and the resulting mic / speaker mute and connection state.
  inputChangeTrigger: {
    kind: 'enum',
    values: ['keyboard_mode', 'voice_mode', 'mic_toggle', 'speaker_toggle'],
  },
  micMuted: { kind: 'boolean' },
  speakerMuted: { kind: 'boolean' },
  connectionStatus: {
    kind: 'enum',
    values: [
      'idle',
      'connecting',
      'connected',
      'disconnecting',
      'disconnected',
      'not-configured',
      'error',
    ],
  },
  // Whether a migration resolved the member's client portal person, and the
  // closed reason code when it did not. Enum-ruled so the key can never carry
  // the person id itself. The reason list mirrors the migration module's
  // ELIGIBLE_PERSON_REASONS plus 'none'; a test there pins the two together.
  eligible_person: { kind: 'enum', values: ['resolved', 'unresolved', 'not_attempted'] },
  eligible_person_reason: {
    kind: 'enum',
    values: [
      'none',
      'provider_not_internaltools',
      'input_incomplete',
      'client_ref_not_internaltools',
      'no_match',
      'needs_review',
      'client_mismatch',
      'person_id_absent',
      'provider_error',
      'deadline',
    ],
  },
  connection_state: {
    kind: 'enum',
    values: [
      'idle',
      'connecting',
      'connected',
      'disconnecting',
      'disconnected',
      'error',
      'not-configured',
    ],
  },
  // Voice session exit and audio context: the client connect-attempt id that
  // joins connect, permission, exit and disconnect events; elapsed times and
  // turn counts at exit; and the output audio route class and volume bucket.
  // The member turn count is `user_turn_count` because any key containing
  // `member` is scrubbed by the standard redactor.
  connect_attempt_id: { kind: 'uuid' },
  ms_since_connect_started: { kind: 'integer' },
  ms_since_connected: { kind: 'integer' },
  ever_connected: { kind: 'boolean' },
  agent_turn_count: { kind: 'integer' },
  user_turn_count: { kind: 'integer' },
  agent_speaking: { kind: 'boolean' },
  audio_route: { kind: 'enum', values: ['speaker', 'receiver', 'wired', 'bluetooth', 'other'] },
  previous_audio_route: {
    kind: 'enum',
    values: ['speaker', 'receiver', 'wired', 'bluetooth', 'other'],
  },
  volume_direction: { kind: 'enum', values: ['up', 'down'] },
  volume_bucket: { kind: 'enum', values: ['muted', 'low', 'medium', 'high', 'max'] },
  volume_changes_coalesced: { kind: 'integer' },
  runtime_session_id: { kind: 'uuid' },
  runtime_room_name: { kind: 'uuid' },
  runtime_practice_cohort: { kind: 'enum', values: ['composed_practice'] },
  runtime_opening_history_stage: {
    kind: 'enum',
    values: [
      'opening_projected',
      'later_turn',
      'history_lost',
      'mode_changed',
      'keyboard_transcript',
    ],
  },
  runtime_opening_history_mode: { kind: 'enum', values: ['voice', 'keyboard'] },
  runtime_opening_history_projected_opening_count: { kind: 'integer' },
  runtime_opening_history_surface_opening_count: { kind: 'integer' },
  runtime_opening_history_projected_message_count: { kind: 'integer' },
  runtime_opening_history_first_message_id: { kind: 'uuid' },
  runtime_opening_history_second_message_id: { kind: 'uuid' },
  runtime_opening_history_selected_message_id: { kind: 'uuid' },
  runtime_first_practice_phase: {
    kind: 'enum',
    values: ['agent_opening', 'first_member_reply', 'terminal_signal', 'completion_presented'],
  },
  runtime_first_practice_source: {
    kind: 'enum',
    values: ['runtime_projection', 'member_input', 'provider_agent_end_call', 'client_completion'],
  },
  runtime_first_practice_first_member_reply_seen: { kind: 'boolean' },
  runtime_first_practice_input_mode: { kind: 'enum', values: ['voice', 'keyboard'] },
  runtime_first_practice_elapsed_ms: { kind: 'number' },
  runtime_first_practice_agent_turn_count: { kind: 'integer' },
  runtime_first_practice_member_turn_count: { kind: 'integer' },
  runtime_mid_session_disconnect_presentation: {
    kind: 'enum',
    values: ['connection_lost', 'agent_ended'],
  },
  runtime_diagnostic_observed_count: { kind: 'integer' },
  runtime_diagnostic_suppressed_count: { kind: 'integer' },
  runtime_diagnostic_overflow_count: { kind: 'integer' },
  runtime_diagnostic_overflow: { kind: 'boolean' },
  runtime_schema_version: {
    kind: 'schema',
  },
  runtime_attempt_id: {
    kind: 'uuid',
  },
  runtime_conversation_attempt_conversation_type: {
    kind: 'enum',
    values: [
      'base',
      'intake',
      'onboarding',
      'check_in',
      'guided_conversation',
      'practice',
      'initial_review',
      'weekly_plan_review',
      'care_team',
      'unknown',
    ],
  },
  runtime_conversation_attempt_launch_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_conversation_attempt_outcome: {
    kind: 'enum',
    values: ['completed', 'failed', 'cancelled', 'fallback'],
  },
  runtime_conversation_attempt_terminal_phase: {
    kind: 'enum',
    values: [
      'connect_requested',
      'assessment_gate',
      'eligibility_gate',
      'session_reset',
      'agent_override',
      'audio_session',
      'context_fetch',
      'credential_fetch',
      'provider_connect',
      'connected',
      'waiting_for_first_response',
      'active',
      'disconnected',
      'recovery',
      'fallback',
      'terminal',
    ],
  },
  runtime_conversation_attempt_failure_class: {
    kind: 'enum',
    values: [
      'none',
      'authentication',
      'timeout',
      'network',
      'transport',
      'provider',
      'audio_session',
      'unknown',
    ],
  },
  runtime_conversation_attempt_last_provider_failure_class: {
    kind: 'enum',
    values: [
      'none',
      'authentication',
      'timeout',
      'network',
      'transport',
      'provider',
      'audio_session',
      'unknown',
    ],
  },
  runtime_conversation_attempt_provider_attempt_count: {
    kind: 'integer',
  },
  runtime_conversation_attempt_connection_count: {
    kind: 'integer',
  },
  runtime_conversation_attempt_disconnect_count: {
    kind: 'integer',
  },
  runtime_conversation_attempt_recovery_attempt_count: {
    kind: 'integer',
  },
  runtime_conversation_attempt_recovery_resolved: {
    kind: 'boolean',
  },
  runtime_conversation_attempt_recovery_exhausted: {
    kind: 'boolean',
  },
  runtime_conversation_attempt_fallback_resolved: {
    kind: 'boolean',
  },
  runtime_conversation_attempt_first_response_received: {
    kind: 'boolean',
  },
  runtime_conversation_attempt_lifecycle_state: {
    kind: 'enum',
    values: ['foreground', 'background', 'unknown'],
  },
  runtime_conversation_attempt_network_state: {
    kind: 'enum',
    values: ['online', 'offline', 'unknown'],
  },
  runtime_conversation_attempt_total_duration_ms: {
    kind: 'number',
  },
  runtime_conversation_attempt_time_to_first_connection_ms: {
    kind: 'number',
  },
  runtime_conversation_attempt_time_to_first_response_ms: {
    kind: 'number',
  },
  runtime_connection_expo_sdk_major: {
    kind: 'integer',
  },
  runtime_connection_attempt_number: {
    kind: 'integer',
  },
  runtime_connection_outcome: {
    kind: 'enum',
    values: ['connected', 'failed', 'cancelled'],
  },
  runtime_connection_lifecycle_state: {
    kind: 'enum',
    values: ['foreground', 'background', 'unknown'],
  },
  runtime_connection_network_state: {
    kind: 'enum',
    values: ['online', 'offline', 'unknown'],
  },
  runtime_connection_provider_failure_category: {
    kind: 'enum',
    values: ['none', 'authentication', 'timeout', 'transport', 'provider', 'unknown'],
  },
  runtime_turn_id: {
    kind: 'uuid',
  },
  runtime_provider_turn_completion_reason: {
    kind: 'enum',
    values: ['stop', 'final_response', 'interruption', 'replaced', 'session_ended'],
  },
  runtime_provider_turn_callback_order_class: {
    kind: 'enum',
    values: [
      'start_before_alignment',
      'alignment_before_start',
      'text_only',
      'delta_before_start',
      'stop_before_start',
    ],
  },
  runtime_provider_turn_audio_text_provider_id_relationship: {
    kind: 'enum',
    values: ['matching', 'different', 'mixed', 'unavailable'],
  },
  runtime_provider_turn_normalized_event_count: {
    kind: 'integer',
  },
  runtime_provider_turn_provider_event_count: {
    kind: 'integer',
  },
  runtime_provider_turn_provider_id_retained_count: {
    kind: 'integer',
  },
  runtime_provider_turn_synthetic_id_count: {
    kind: 'integer',
  },
  runtime_provider_turn_source_counts_elevenlabs_sdk: {
    kind: 'integer',
  },
  runtime_provider_turn_source_counts_livekit: {
    kind: 'integer',
  },
  runtime_provider_turn_pre_start_alignment_lead_ms: {
    kind: 'number',
  },
  runtime_provider_turn_pre_start_audio_render_lead_ms: {
    kind: 'number',
  },
  runtime_provider_turn_pre_start_rendered_duration_ms: {
    kind: 'number',
  },
  runtime_provider_turn_post_stop_alignment_count: {
    kind: 'integer',
  },
  runtime_provider_turn_first_post_stop_alignment_delay_ms: {
    kind: 'number',
  },
  runtime_provider_turn_ambiguous_post_stop_render_progress_count: {
    kind: 'integer',
  },
  runtime_provider_turn_ambiguous_post_stop_rendered_duration_ms: {
    kind: 'number',
  },
  runtime_provider_turn_discard_counts_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_counts_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_counts_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_counts_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_counts_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_counts_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_start_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_start_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_start_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_start_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_start_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_start_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_delta_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_delta_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_delta_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_delta_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_delta_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_delta_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_stop_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_stop_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_stop_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_stop_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_stop_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_response_stop_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_interruption_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_interruption_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_interruption_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_interruption_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_interruption_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_interruption_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_alignment_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_alignment_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_alignment_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_alignment_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_alignment_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_alignment_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_livekit_data_duplicate: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_livekit_data_late: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_livekit_data_empty: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_livekit_data_out_of_order: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_livekit_data_malformed: {
    kind: 'integer',
  },
  runtime_provider_turn_discard_callback_counts_livekit_data_unsupported: {
    kind: 'integer',
  },
  runtime_provider_turn_discarded_callback_class: {
    kind: 'enum',
    values: [
      'response_start',
      'response_delta',
      'response_stop',
      'interruption',
      'alignment',
      'livekit_data',
      'mixed',
      'unavailable',
    ],
  },
  runtime_provider_turn_discarded_callback_count: {
    kind: 'integer',
  },
  runtime_provider_turn_preceding_callback_order_class: {
    kind: 'enum',
    values: [
      'start_before_alignment',
      'alignment_before_start',
      'text_only',
      'delta_before_start',
      'stop_before_start',
      'mixed',
      'unavailable',
    ],
  },
  runtime_provider_turn_adapter_turn_state: {
    kind: 'enum',
    values: [
      'active',
      'completed',
      'interrupted',
      'cancelled',
      'no_current_turn',
      'mixed',
      'unavailable',
    ],
  },
  runtime_provider_turn_discarded_callback_identity_relationship: {
    kind: 'enum',
    values: ['matching', 'different', 'mixed', 'unavailable'],
  },
  runtime_provider_turn_reconciliation_action: {
    kind: 'enum',
    values: [
      'normalized',
      'buffered',
      'reconciled',
      'rejected',
      'not_applicable',
      'mixed',
      'unavailable',
    ],
  },
  runtime_provider_turn_reconciliation_resolution: {
    kind: 'enum',
    values: [
      'accepted',
      'discarded_out_of_order',
      'discarded_other',
      'not_applicable',
      'mixed',
      'unavailable',
    ],
  },
  runtime_provider_turn_terminal_trigger: {
    kind: 'enum',
    values: [
      'response_stop',
      'final_response',
      'interruption',
      'replacement',
      'session_end',
      'unavailable',
    ],
  },
  runtime_message_id: {
    kind: 'uuid',
  },
  runtime_turn_render_started_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_turn_render_final_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_turn_render_mode_transition_count: {
    kind: 'integer',
  },
  runtime_turn_render_lifecycle: {
    kind: 'enum',
    values: ['finalized', 'interrupted', 'cancelled', 'failed'],
  },
  runtime_turn_render_segment_count: {
    kind: 'integer',
  },
  runtime_turn_render_authoritative_character_count: {
    kind: 'integer',
  },
  runtime_turn_render_revealed_character_count: {
    kind: 'integer',
  },
  runtime_turn_render_missing_character_count: {
    kind: 'integer',
  },
  runtime_turn_render_authoritative_response_received: {
    kind: 'boolean',
  },
  runtime_turn_render_message_target_changed: {
    kind: 'boolean',
  },
  runtime_turn_render_final_reveal_complete: {
    kind: 'boolean',
  },
  runtime_turn_reconciliation_terminal_authoritative_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_terminal_revealed_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_terminal_missing_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_terminal_authoritative_response_received: {
    kind: 'boolean',
  },
  runtime_turn_reconciliation_terminal_alignment_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_grace_authoritative_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_grace_revealed_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_grace_missing_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_grace_authoritative_response_received: {
    kind: 'boolean',
  },
  runtime_turn_reconciliation_grace_alignment_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_post_terminal_alignment_growth_character_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_authoritative_response_delay_after_terminal_ms: {
    kind: 'number',
  },
  runtime_turn_reconciliation_audio_playback_started: {
    kind: 'boolean',
  },
  runtime_turn_reconciliation_related_tool_call_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_grace_duration_ms: {
    kind: 'number',
  },
  runtime_turn_reconciliation_outcome: {
    kind: 'enum',
    values: [
      'complete_at_terminal',
      'recovered_after_terminal',
      'incomplete_after_grace',
      'contentless_tool_turn',
    ],
  },
  runtime_turn_reconciliation_provider_callback_order_class: {
    kind: 'enum',
    values: [
      'start_before_alignment',
      'alignment_before_start',
      'text_only',
      'delta_before_start',
      'stop_before_start',
      'mixed',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_discarded_callback_class: {
    kind: 'enum',
    values: [
      'response_start',
      'response_delta',
      'response_stop',
      'interruption',
      'alignment',
      'livekit_data',
      'mixed',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_discarded_callback_count: {
    kind: 'integer',
  },
  runtime_turn_reconciliation_preceding_callback_order_class: {
    kind: 'enum',
    values: [
      'start_before_alignment',
      'alignment_before_start',
      'text_only',
      'delta_before_start',
      'stop_before_start',
      'mixed',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_adapter_turn_state: {
    kind: 'enum',
    values: [
      'active',
      'completed',
      'interrupted',
      'cancelled',
      'no_current_turn',
      'mixed',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_discarded_callback_identity_relationship: {
    kind: 'enum',
    values: ['matching', 'different', 'mixed', 'unavailable'],
  },
  runtime_turn_reconciliation_reconciliation_action: {
    kind: 'enum',
    values: [
      'normalized',
      'buffered',
      'reconciled',
      'rejected',
      'not_applicable',
      'mixed',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_reconciliation_resolution: {
    kind: 'enum',
    values: [
      'accepted',
      'discarded_out_of_order',
      'discarded_other',
      'not_applicable',
      'mixed',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_terminal_trigger: {
    kind: 'enum',
    values: [
      'response_stop',
      'final_response',
      'interruption',
      'replacement',
      'session_end',
      'unavailable',
    ],
  },
  runtime_turn_reconciliation_provider_generation_relationship: {
    kind: 'enum',
    values: ['matching', 'different', 'unavailable'],
  },
  runtime_turn_reconciliation_terminal_playback_authority_state: {
    kind: 'enum',
    values: ['active', 'inactive', 'unavailable'],
  },
  runtime_turn_reconciliation_grace_playback_authority_state: {
    kind: 'enum',
    values: ['active', 'inactive', 'unavailable'],
  },
  runtime_typewriter_drift_native_progress_sample_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_native_timestamp_unavailable_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_max_native_callback_delay_ms: {
    kind: 'number',
  },
  runtime_typewriter_drift_max_native_progress_age_ms: {
    kind: 'number',
  },
  runtime_typewriter_drift_anchor_correction_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_max_anchor_correction_ms: {
    kind: 'number',
  },
  runtime_typewriter_drift_sample_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_max_absolute_drift_ms: {
    kind: 'number',
  },
  runtime_typewriter_drift_duration_outside_tolerance_ms: {
    kind: 'number',
  },
  runtime_typewriter_drift_recovered_by_turn_end: {
    kind: 'boolean',
  },
  runtime_typewriter_drift_timing_source: {
    kind: 'enum',
    values: ['alignment', 'fallback', 'unavailable'],
  },
  runtime_typewriter_drift_started_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_typewriter_drift_final_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_typewriter_drift_mode_transition_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_partial_word_reveal_prevention_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_max_word_reveal_lag_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_word_boundary_remap_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_dash_boundary_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_max_word_reveal_burst_count: {
    kind: 'integer',
  },
  runtime_typewriter_drift_tolerance_status: {
    kind: 'enum',
    values: ['provisional_physical_baseline_pending'],
  },
  runtime_typewriter_ui_schedule_policy_version: {
    kind: 'enum',
    values: ['render_progress_word_v1'],
  },
  runtime_typewriter_ui_schedule_timing_source: {
    kind: 'enum',
    values: ['alignment', 'fallback', 'unavailable'],
  },
  runtime_typewriter_ui_schedule_text_source: {
    kind: 'enum',
    values: ['authoritative', 'alignment', 'terminal_authoritative'],
  },
  runtime_typewriter_ui_schedule_dash_boundary_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_schedule_contraction_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_ordinal_text_mutation_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_visible_prefix_rewrite_attempt_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_scheduled_word_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_schedule_update_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_schedule_growth_word_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_late_schedule_update_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_late_schedule_growth_word_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_schedule_availability_lag_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_alignment_text_schedule_update_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_alignment_scheduled_word_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_js_commit_to_next_frame_callback_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_max_ui_completion_to_js_callback_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_max_ui_observation_to_js_callback_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_ui_completion_ack_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_ui_observation_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_ui_reveal_burst_word_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_ui_frame_gap_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_ui_frame_gap_catch_up_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_ui_visible_playback_lag_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_follow_scroll_retarget_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_follow_scroll_member_disable_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_follow_scroll_member_enable_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_max_follow_scroll_distance_from_bottom_while_enabled: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_configured_fade_duration_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_app_nonactive_episode_count: {
    kind: 'integer',
  },
  runtime_typewriter_ui_schedule_total_app_nonactive_duration_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_max_app_nonactive_duration_ms: {
    kind: 'number',
  },
  runtime_typewriter_ui_schedule_animation_thread: {
    kind: 'enum',
    values: ['reanimated_ui', 'js', 'none'],
  },
  runtime_typewriter_ui_schedule_render_surface: {
    kind: 'enum',
    values: [
      'independent_word_fade_v3',
      'independent_word_fade_static_v4',
      'independent_word_fade_static_v5',
    ],
  },
  runtime_typewriter_ui_schedule_word_fade_completed_count: { kind: 'integer' },
  runtime_typewriter_ui_schedule_word_static_committed_count: { kind: 'integer' },
  runtime_typewriter_ui_schedule_word_fade_cancelled_count: { kind: 'integer' },
  runtime_typewriter_ui_schedule_word_opacity_regression_count: { kind: 'integer' },
  runtime_typewriter_ui_schedule_word_remount_count: { kind: 'integer' },
  runtime_word_visibility_phase: {
    kind: 'enum',
    values: [
      'renderer_attached',
      'first_static_commit',
      'opacity_regressed',
      'settled_word_remounted',
    ],
  },
  runtime_word_visibility_word_index: { kind: 'integer' },
  runtime_word_visibility_progress: { kind: 'number' },
  runtime_word_visibility_elapsed_ms: { kind: 'number' },
  runtime_word_visibility_schedule_version: { kind: 'integer' },
  runtime_word_visibility_render_surface: {
    kind: 'enum',
    values: ['independent_word_fade_static_v4', 'independent_word_fade_static_v5'],
  },
  runtime_typewriter_ui_schedule_outcome: {
    kind: 'enum',
    values: ['terminal', 'replaced', 'unmounted'],
  },
  runtime_text_send_connection_state_at_submit: {
    kind: 'enum',
    values: ['connected', 'connecting', 'disconnected'],
  },
  runtime_text_send_delivery_status: {
    kind: 'enum',
    values: ['dispatched', 'failed', 'skipped'],
  },
  runtime_text_send_attempt_count: {
    kind: 'integer',
  },
  runtime_text_send_pending_duration_ms: {
    kind: 'number',
  },
  runtime_text_send_retry_result: {
    kind: 'enum',
    values: ['not_applicable', 'pending', 'succeeded', 'failed'],
  },
  runtime_text_send_contextual_update_result: {
    kind: 'enum',
    values: ['not_applicable', 'dispatched', 'failed', 'skipped'],
  },
  runtime_text_send_qualified_typed_interruption: {
    kind: 'boolean',
  },
  runtime_text_send_contextual_update_attempted: {
    kind: 'boolean',
  },
  runtime_tool_call_id: {
    kind: 'uuid',
  },
  runtime_tool_call_tool_name: {
    kind: 'enum',
    values: [
      'advance_flow',
      'go_back',
      'set_state',
      'toggle_state_membership',
      'clear_state_unless_included',
      'complete_intake_plan',
      'request_microphone_permission',
      'start_intake_conversation',
      'continue_without_sona',
      'open_app_settings',
      'open_checkin_reminder_picker',
      'toggle_checkin_reminder',
      'calculate_week_summary',
      'derive_context_rows',
      'derive_summary_bullets',
      'save_app_onboarding_step',
      'save_flow_session',
      'submit_feedback',
      'show_progress_chart',
      'show_options',
      'show_upcoming_appointment',
      'log_insight',
      'navigate_to_screen',
      'open_chat',
      'open_practice',
      'open_care_booking',
      'join_care_call',
      'open_care_call_details',
      'open_care_team_chat',
      'open_quit_date_picker',
      'open_cbt_module',
      'open_checkin',
      'open_rewards',
      'open_check_ins',
      'open_guided_conversations',
      'open_weekly_plan_review',
      'open_ai_consent_gate',
      'open_bam',
      'open_care_team_introduction',
      'open_care_tab',
      'open_care_upcoming_calls',
      'open_care_call_details',
      'complete_agent_session',
      'toggle_opt_in',
      'suggest_end_call',
      'set_weekly_plan',
      'save_weekly_plan_topics',
      'complete_plan_review',
      'save_initial_review_check_ins',
      'continue_migration_review',
      'capture_migration_registration_consent',
      'open_migration_registration_document',
      'retry_migration_registration_consent',
      'sign_out_migration_registration_consent',
      'record_migration_ai_consent',
      'retry_migration_preparation',
      'book_care_team_call',
      'care_booking_step',
      'select_show_option',
      'build_practice',
      'get_check_ins',
      'search_help',
      'escalate_crisis',
      'start_assessment',
      'save_assessment_answer',
      'evaluate_assessment',
      'complete_assessment',
      'request_opt_in',
      'record_care_team_interest',
      'unknown_tool',
    ],
  },
  runtime_tool_call_status: {
    kind: 'enum',
    values: ['succeeded', 'failed'],
  },
  runtime_tool_call_tool_call_id_source: {
    kind: 'enum',
    values: ['provider', 'synthetic'],
  },
  runtime_tool_call_tool_class: {
    kind: 'enum',
    values: [
      'client_ui_only',
      'server_authoritative_with_client_projection',
      'existing_client_compatibility',
      'tanstack_owned_mutation',
      'unsupported',
    ],
  },
  runtime_tool_call_persistence_path: {
    kind: 'enum',
    values: ['server_authoritative', 'client_api', 'query_mutation', 'none'],
  },
  runtime_tool_call_received_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_tool_call_presentation_target: {
    kind: 'enum',
    values: ['genui_inline', 'none', 'genui_screen', 'keyboard_transcript'],
  },
  runtime_anchor_turn_id: {
    kind: 'uuid',
  },
  runtime_tool_call_delivery_count: {
    kind: 'integer',
  },
  runtime_tool_call_effect_execution_count: {
    kind: 'integer',
  },
  runtime_tool_call_anchored_to_turn: {
    kind: 'boolean',
  },
  runtime_tool_call_stale_result_rejected: {
    kind: 'boolean',
  },
  runtime_tool_call_mutation_cache_result: {
    kind: 'enum',
    values: ['not_applicable', 'not_attempted', 'succeeded', 'failed', 'optimistic', 'deferred'],
  },
  runtime_tool_call_projection_matched: {
    kind: 'boolean',
  },
  runtime_tool_replay_tool_name: {
    kind: 'enum',
    values: [
      'advance_flow',
      'go_back',
      'set_state',
      'toggle_state_membership',
      'clear_state_unless_included',
      'complete_intake_plan',
      'request_microphone_permission',
      'start_intake_conversation',
      'continue_without_sona',
      'open_app_settings',
      'open_checkin_reminder_picker',
      'toggle_checkin_reminder',
      'calculate_week_summary',
      'derive_context_rows',
      'derive_summary_bullets',
      'save_app_onboarding_step',
      'save_flow_session',
      'submit_feedback',
      'show_progress_chart',
      'show_options',
      'show_upcoming_appointment',
      'log_insight',
      'navigate_to_screen',
      'open_chat',
      'open_practice',
      'open_care_booking',
      'join_care_call',
      'open_care_call_details',
      'open_care_team_chat',
      'open_quit_date_picker',
      'open_cbt_module',
      'open_checkin',
      'open_rewards',
      'open_check_ins',
      'open_guided_conversations',
      'open_weekly_plan_review',
      'open_ai_consent_gate',
      'open_bam',
      'open_care_team_introduction',
      'open_care_tab',
      'open_care_upcoming_calls',
      'open_care_call_details',
      'complete_agent_session',
      'toggle_opt_in',
      'suggest_end_call',
      'set_weekly_plan',
      'save_weekly_plan_topics',
      'complete_plan_review',
      'save_initial_review_check_ins',
      'continue_migration_review',
      'capture_migration_registration_consent',
      'open_migration_registration_document',
      'retry_migration_registration_consent',
      'sign_out_migration_registration_consent',
      'record_migration_ai_consent',
      'retry_migration_preparation',
      'book_care_team_call',
      'care_booking_step',
      'select_show_option',
      'build_practice',
      'get_check_ins',
      'search_help',
      'escalate_crisis',
      'start_assessment',
      'save_assessment_answer',
      'evaluate_assessment',
      'complete_assessment',
      'request_opt_in',
      'record_care_team_interest',
      'unknown_tool',
    ],
  },
  runtime_tool_replay_tool_call_id_source: {
    kind: 'enum',
    values: ['provider', 'synthetic'],
  },
  runtime_tool_replay_received_mode: {
    kind: 'enum',
    values: ['voice', 'text'],
  },
  runtime_tool_replay_replay_timing: {
    kind: 'enum',
    values: ['before_terminal', 'after_terminal'],
  },
  runtime_tool_replay_delivery_count: {
    kind: 'integer',
  },
  runtime_tool_replay_effect_execution_count: {
    kind: 'integer',
  },
  runtime_generation_rejection_event_type: {
    kind: 'enum',
    values: [
      'session.history_hydrated',
      'session.mode_changed',
      'session.closed',
      'agent.turn_started',
      'agent.text_delta',
      'agent.transcript_reconciled',
      'agent.audio_alignment',
      'agent.playback_started',
      'agent.playback_progressed',
      'agent.typewriter_cursor_revealed',
      'agent.playback_observed',
      'agent.playback_completed',
      'agent.turn_ended',
      'member.typed_message_submitted',
      'member.typed_message_retry_requested',
      'member.typed_message_delivery_resolved',
      'member.voice_transcript_received',
      'member.voice_interruption',
      'tool.call_received',
      'tool.presentation_ready',
      'tool.call_resolved',
      'other',
    ],
  },
  runtime_generation_rejection_event_source: {
    kind: 'enum',
    values: ['elevenlabs_sdk', 'livekit', 'client', 'effect', 'clock'],
  },
  runtime_generation_rejection_event_generation: {
    kind: 'integer',
  },
  runtime_generation_rejection_active_generation: {
    kind: 'integer',
  },
  runtime_generation_rejection_anomaly_code: {
    kind: 'enum',
    values: [
      'stale_generation_event_rejected',
      'closed_session_event_rejected',
      'duplicate_tool_call_suppressed',
      'unknown_turn_event_rejected',
      'late_turn_event_rejected',
      'alignment_length_mismatch',
      'tool_result_without_call',
      'tool_result_after_terminal_state',
    ],
  },
  runtime_generation_rejection_rejected_class: {
    kind: 'enum',
    values: ['runtime_event', 'provider_callback', 'timer', 'tool_result', 'typed_result'],
  },
  runtime_generation_rejection_runtime_identity_mismatch: {
    kind: 'boolean',
  },
  runtime_generation_rejection_teardown_age_ms: {
    kind: 'number',
  },
  runtime_session_lifecycle_outcome: {
    kind: 'enum',
    values: [
      'close_during_start',
      'late_connect_suppressed',
      'late_callback_suppressed',
      'agent_end_call_signal_forwarded',
      'deferred_provider_teardown_abandoned',
      'deferred_provider_teardown_owner_conflict',
      'startup_cancelled',
    ],
  },
  runtime_session_lifecycle_guard_layer: {
    kind: 'enum',
    values: ['app', 'transport', 'unavailable'],
  },
  runtime_session_lifecycle_provider_start_generation: {
    kind: 'integer',
  },
  runtime_session_lifecycle_active_provider_start_generation: {
    kind: 'integer',
  },
  runtime_session_lifecycle_lifecycle_state: {
    kind: 'enum',
    values: ['foreground', 'background', 'unknown'],
  },
  runtime_session_lifecycle_callback_class: {
    kind: 'enum',
    values: [
      'audio_render_progress',
      'remote_track_subscribed',
      'livekit_data',
      'room_signal_reconnecting',
      'room_reconnecting',
      'room_reconnected',
      'provider_error_during_teardown',
      'other',
      'unavailable',
    ],
  },
  runtime_session_lifecycle_runtime_identity_mismatch: {
    kind: 'boolean',
  },
  runtime_session_lifecycle_teardown_age_ms: {
    kind: 'number',
  },
  runtime_session_lifecycle_provider_event_type: {
    kind: 'enum',
    values: ['agent_tool_response', 'agent_tool_response_full_payload', 'other', 'unavailable'],
  },
  runtime_session_lifecycle_attempt_number: {
    kind: 'integer',
  },
  runtime_session_lifecycle_close_source: {
    kind: 'enum',
    values: ['member_end', 'imperative_end', 'unmount', 'startup_timeout', 'unavailable'],
  },
  runtime_session_lifecycle_startup_stage: {
    kind: 'enum',
    values: [
      'idle',
      'assessment_gate',
      'eligibility_gate',
      'session_reset',
      'agent_override',
      'audio_session',
      'context_fetch',
      'credential_fetch',
      'sdk_start',
      'connected',
      'cancelled',
    ],
  },
  runtime_session_lifecycle_startup_generation: {
    kind: 'integer',
  },
  runtime_session_lifecycle_active_startup_generation: {
    kind: 'integer',
  },
  runtime_episode_id: {
    kind: 'uuid',
  },
  runtime_service_instance_id: {
    kind: 'uuid',
  },
  runtime_background_episode_platform: {
    kind: 'enum',
    values: ['ios', 'android', 'web', 'unknown'],
  },
  runtime_background_episode_lifecycle_cause: {
    kind: 'enum',
    values: ['lock', 'app_switch', 'system_interruption', 'unknown'],
  },
  runtime_background_episode_service_start_state: {
    kind: 'enum',
    values: [
      'unavailable',
      'idle',
      'start_requested',
      'running',
      'end_requested',
      'stopped',
      'failed',
      'not_applicable',
    ],
  },
  runtime_background_episode_service_failure_class: {
    kind: 'enum',
    values: [
      'none',
      'native_module_unavailable',
      'notification_permission_denied',
      'invalid_identity',
      'foreground_service_start_not_allowed',
      'foreground_service_stop_failed',
      'security_exception',
      'native_exception',
      'unknown',
      'not_applicable',
    ],
  },
  runtime_background_episode_notification_permission_granted: {
    kind: 'boolean',
  },
  runtime_background_episode_background_duration_ms: {
    kind: 'number',
  },
  runtime_background_episode_disconnect_observed_at: {
    kind: 'enum',
    values: ['background', 'foreground', 'unknown'],
  },
  runtime_background_episode_disconnect_observed_after_background_ms: {
    kind: 'number',
  },
  runtime_background_episode_connected_at_entry: {
    kind: 'boolean',
  },
  runtime_background_episode_connected_at_return: {
    kind: 'boolean',
  },
  runtime_background_episode_microphone_input_observed: {
    kind: 'boolean',
  },
  runtime_background_episode_agent_output_observed: {
    kind: 'boolean',
  },
  runtime_background_episode_microphone_input_observed_in_background: {
    kind: 'boolean',
  },
  runtime_background_episode_agent_output_observed_in_background: {
    kind: 'boolean',
  },
  runtime_background_episode_provider_response_observed_in_background: {
    kind: 'boolean',
  },
  runtime_background_episode_last_provider_response_after_background_ms: {
    kind: 'number',
  },
  runtime_background_episode_first_usable_response_after_return: {
    kind: 'boolean',
  },
  runtime_background_episode_recovery_attempted: {
    kind: 'boolean',
  },
  runtime_background_episode_recovery_resolved: {
    kind: 'boolean',
  },
  runtime_background_episode_fallback_attempted: {
    kind: 'boolean',
  },
  runtime_background_episode_battery_optimization_class: {
    kind: 'enum',
    values: ['normal', 'optimized', 'restricted', 'unknown', 'not_applicable'],
  },
  runtime_background_episode_outcome: {
    kind: 'enum',
    values: ['alive_and_usable', 'recovered', 'terminal_failure', 'intentionally_ended', 'unknown'],
  },
  runtime_playback_handoff_outgoing_turn_id: { kind: 'uuid' },
  runtime_playback_handoff_outcome: { kind: 'enum', values: ['selected', 'excluded', 'disposed'] },
  runtime_playback_handoff_reason: {
    kind: 'enum',
    values: [
      'scheduled_boundary',
      'member_turn',
      'cancelled',
      'interrupted',
      'failed',
      'superseded',
      'session_closed',
    ],
  },
  runtime_playback_handoff_attribution: { kind: 'enum', values: ['estimated'] },
  runtime_playback_handoff_boundary_reason: {
    kind: 'enum',
    values: ['no_clock', 'schedule_pending', 'alignment_dwell', 'eligible'],
  },
  runtime_playback_handoff_anchor_source: {
    kind: 'enum',
    values: ['native_output', 'alignment_fallback'],
  },
  runtime_playback_handoff_hold_duration_ms: { kind: 'number' },
  runtime_playback_handoff_scheduled_boundary_ms: { kind: 'number' },
  runtime_playback_handoff_alignment_duration_ms: { kind: 'number' },
  runtime_playback_handoff_reveal_boundary_ms: { kind: 'number' },
  runtime_playback_handoff_evaluated_position_ms: { kind: 'number' },
  runtime_playback_handoff_last_alignment_age_ms: { kind: 'number' },
  runtime_playback_handoff_evaluation_age_ms: { kind: 'number' },
  runtime_playback_handoff_max_evaluation_gap_ms: { kind: 'number' },
  runtime_playback_handoff_evaluation_count: { kind: 'integer' },
  runtime_playback_handoff_ambiguous_progress_count: { kind: 'integer' },
  runtime_id: {
    kind: 'uuid',
  },
  runtime_generation: {
    kind: 'integer',
  },
  runtime_provider: {
    kind: 'enum',
    values: ['elevenlabs_saas', 'backend2_vpc'],
  },
  runtime_policy_version: {
    kind: 'enum',
    values: ['render_progress_word_v1'],
  },
  runtime_platform: {
    kind: 'enum',
    values: ['ios', 'android', 'web', 'unknown'],
  },
  runtime_platform_version: {
    kind: 'version',
  },
  runtime_fabric_enabled: {
    kind: 'boolean',
  },
  runtime_turbo_module_proxy_available: {
    kind: 'boolean',
  },
  runtime_native_fabric_ui_manager_available: {
    kind: 'boolean',
  },
  runtime_update_id: {
    kind: 'uuid',
  },
  runtime_app_build_number: {
    kind: 'build',
  },
  runtime_app_version: {
    kind: 'version',
  },
  runtime_native_runtime_version: {
    kind: 'version',
  },
  runtime_caption_visibility_boundary: {
    kind: 'enum',
    values: ['selected_target', 'projection', 'reveal', 'surface'],
  },
  runtime_caption_visibility_reason: {
    kind: 'enum',
    values: [
      'unsupported_target_replacement',
      'projection_lost',
      'prefix_regressed',
      'surface_replaced_without_retirement',
      'schedule_missing_with_audio',
    ],
  },
  runtime_caption_visibility_observation_source: {
    kind: 'enum',
    values: [
      'runtime_projection',
      'committed_selection',
      'ui_install_snapshot',
      'ui_reveal_callback',
    ],
  },
  runtime_caption_visibility_coverage: {
    kind: 'enum',
    values: ['state_boundaries_observed', 'projection_only', 'incomplete'],
  },
  runtime_caption_visibility_outcome: {
    kind: 'enum',
    values: ['healthy_observed', 'anomalous_observed', 'insufficient_coverage'],
  },
  runtime_caption_visibility_sequence: { kind: 'integer' },
  runtime_caption_visibility_elapsed_ms: { kind: 'number' },
  runtime_caption_visibility_prefix_before_count: { kind: 'integer' },
  runtime_caption_visibility_prefix_after_count: { kind: 'integer' },
  runtime_caption_visibility_selected_target_changed: { kind: 'boolean' },
  runtime_caption_visibility_projection_present: { kind: 'boolean' },
  runtime_caption_visibility_renderer_generation: { kind: 'integer' },
  runtime_caption_visibility_projection_revision: { kind: 'integer' },
  runtime_caption_visibility_expected_transition_count: { kind: 'integer' },
  runtime_caption_visibility_unexpected_transition_count: { kind: 'integer' },
  runtime_caption_visibility_candidate_rewrite_count: { kind: 'integer' },
  runtime_caption_visibility_observed_regression_count: { kind: 'integer' },
  runtime_caption_visibility_monitor_duration_ms: { kind: 'number' },
} as const satisfies Record<string, RuntimeAttributeRule>;

/** Source-leaf aliases are explicit so completeness can be audited independently of emitters. */
export const RUNTIME_FIELD_MAP = {
  playback_handoff: {
    turn_id: 'runtime_turn_id',
    outgoing_turn_id: 'runtime_playback_handoff_outgoing_turn_id',
    outcome: 'runtime_playback_handoff_outcome',
    reason: 'runtime_playback_handoff_reason',
    attribution: 'runtime_playback_handoff_attribution',
    boundary_reason: 'runtime_playback_handoff_boundary_reason',
    anchor_source: 'runtime_playback_handoff_anchor_source',
    hold_duration_ms: 'runtime_playback_handoff_hold_duration_ms',
    scheduled_boundary_ms: 'runtime_playback_handoff_scheduled_boundary_ms',
    alignment_duration_ms: 'runtime_playback_handoff_alignment_duration_ms',
    reveal_boundary_ms: 'runtime_playback_handoff_reveal_boundary_ms',
    evaluated_position_ms: 'runtime_playback_handoff_evaluated_position_ms',
    last_alignment_age_ms: 'runtime_playback_handoff_last_alignment_age_ms',
    evaluation_age_ms: 'runtime_playback_handoff_evaluation_age_ms',
    max_evaluation_gap_ms: 'runtime_playback_handoff_max_evaluation_gap_ms',
    evaluation_count: 'runtime_playback_handoff_evaluation_count',
    ambiguous_progress_count: 'runtime_playback_handoff_ambiguous_progress_count',
  },

  conversation_attempt: {
    schema_version: 'runtime_schema_version',
    attempt_id: 'runtime_attempt_id',
    conversation_type: 'runtime_conversation_attempt_conversation_type',
    launch_mode: 'runtime_conversation_attempt_launch_mode',
    outcome: 'runtime_conversation_attempt_outcome',
    terminal_phase: 'runtime_conversation_attempt_terminal_phase',
    failure_class: 'runtime_conversation_attempt_failure_class',
    last_provider_failure_class: 'runtime_conversation_attempt_last_provider_failure_class',
    provider_attempt_count: 'runtime_conversation_attempt_provider_attempt_count',
    connection_count: 'runtime_conversation_attempt_connection_count',
    disconnect_count: 'runtime_conversation_attempt_disconnect_count',
    recovery_attempt_count: 'runtime_conversation_attempt_recovery_attempt_count',
    recovery_resolved: 'runtime_conversation_attempt_recovery_resolved',
    recovery_exhausted: 'runtime_conversation_attempt_recovery_exhausted',
    fallback_resolved: 'runtime_conversation_attempt_fallback_resolved',
    first_response_received: 'runtime_conversation_attempt_first_response_received',
    lifecycle_state: 'runtime_conversation_attempt_lifecycle_state',
    network_state: 'runtime_conversation_attempt_network_state',
    total_duration_ms: 'runtime_conversation_attempt_total_duration_ms',
    time_to_first_connection_ms: 'runtime_conversation_attempt_time_to_first_connection_ms',
    time_to_first_response_ms: 'runtime_conversation_attempt_time_to_first_response_ms',
  },
  connection: {
    attempt_id: 'runtime_attempt_id',
    expo_sdk_major: 'runtime_connection_expo_sdk_major',
    attempt_number: 'runtime_connection_attempt_number',
    outcome: 'runtime_connection_outcome',
    lifecycle_state: 'runtime_connection_lifecycle_state',
    network_state: 'runtime_connection_network_state',
    provider_failure_category: 'runtime_connection_provider_failure_category',
  },
  provider_turn: {
    turn_id: 'runtime_turn_id',
    completion_reason: 'runtime_provider_turn_completion_reason',
    callback_order_class: 'runtime_provider_turn_callback_order_class',
    audio_text_provider_id_relationship:
      'runtime_provider_turn_audio_text_provider_id_relationship',
    normalized_event_count: 'runtime_provider_turn_normalized_event_count',
    provider_event_count: 'runtime_provider_turn_provider_event_count',
    provider_id_retained_count: 'runtime_provider_turn_provider_id_retained_count',
    synthetic_id_count: 'runtime_provider_turn_synthetic_id_count',
    'source_counts.elevenlabs_sdk': 'runtime_provider_turn_source_counts_elevenlabs_sdk',
    'source_counts.livekit': 'runtime_provider_turn_source_counts_livekit',
    pre_start_alignment_lead_ms: 'runtime_provider_turn_pre_start_alignment_lead_ms',
    pre_start_audio_render_lead_ms: 'runtime_provider_turn_pre_start_audio_render_lead_ms',
    pre_start_rendered_duration_ms: 'runtime_provider_turn_pre_start_rendered_duration_ms',
    post_stop_alignment_count: 'runtime_provider_turn_post_stop_alignment_count',
    first_post_stop_alignment_delay_ms: 'runtime_provider_turn_first_post_stop_alignment_delay_ms',
    ambiguous_post_stop_render_progress_count:
      'runtime_provider_turn_ambiguous_post_stop_render_progress_count',
    ambiguous_post_stop_rendered_duration_ms:
      'runtime_provider_turn_ambiguous_post_stop_rendered_duration_ms',
    'discard_counts.duplicate': 'runtime_provider_turn_discard_counts_duplicate',
    'discard_counts.late': 'runtime_provider_turn_discard_counts_late',
    'discard_counts.empty': 'runtime_provider_turn_discard_counts_empty',
    'discard_counts.out_of_order': 'runtime_provider_turn_discard_counts_out_of_order',
    'discard_counts.malformed': 'runtime_provider_turn_discard_counts_malformed',
    'discard_counts.unsupported': 'runtime_provider_turn_discard_counts_unsupported',
    'discard_callback_counts.response_start.duplicate':
      'runtime_provider_turn_discard_callback_counts_response_start_duplicate',
    'discard_callback_counts.response_start.late':
      'runtime_provider_turn_discard_callback_counts_response_start_late',
    'discard_callback_counts.response_start.empty':
      'runtime_provider_turn_discard_callback_counts_response_start_empty',
    'discard_callback_counts.response_start.out_of_order':
      'runtime_provider_turn_discard_callback_counts_response_start_out_of_order',
    'discard_callback_counts.response_start.malformed':
      'runtime_provider_turn_discard_callback_counts_response_start_malformed',
    'discard_callback_counts.response_start.unsupported':
      'runtime_provider_turn_discard_callback_counts_response_start_unsupported',
    'discard_callback_counts.response_delta.duplicate':
      'runtime_provider_turn_discard_callback_counts_response_delta_duplicate',
    'discard_callback_counts.response_delta.late':
      'runtime_provider_turn_discard_callback_counts_response_delta_late',
    'discard_callback_counts.response_delta.empty':
      'runtime_provider_turn_discard_callback_counts_response_delta_empty',
    'discard_callback_counts.response_delta.out_of_order':
      'runtime_provider_turn_discard_callback_counts_response_delta_out_of_order',
    'discard_callback_counts.response_delta.malformed':
      'runtime_provider_turn_discard_callback_counts_response_delta_malformed',
    'discard_callback_counts.response_delta.unsupported':
      'runtime_provider_turn_discard_callback_counts_response_delta_unsupported',
    'discard_callback_counts.response_stop.duplicate':
      'runtime_provider_turn_discard_callback_counts_response_stop_duplicate',
    'discard_callback_counts.response_stop.late':
      'runtime_provider_turn_discard_callback_counts_response_stop_late',
    'discard_callback_counts.response_stop.empty':
      'runtime_provider_turn_discard_callback_counts_response_stop_empty',
    'discard_callback_counts.response_stop.out_of_order':
      'runtime_provider_turn_discard_callback_counts_response_stop_out_of_order',
    'discard_callback_counts.response_stop.malformed':
      'runtime_provider_turn_discard_callback_counts_response_stop_malformed',
    'discard_callback_counts.response_stop.unsupported':
      'runtime_provider_turn_discard_callback_counts_response_stop_unsupported',
    'discard_callback_counts.interruption.duplicate':
      'runtime_provider_turn_discard_callback_counts_interruption_duplicate',
    'discard_callback_counts.interruption.late':
      'runtime_provider_turn_discard_callback_counts_interruption_late',
    'discard_callback_counts.interruption.empty':
      'runtime_provider_turn_discard_callback_counts_interruption_empty',
    'discard_callback_counts.interruption.out_of_order':
      'runtime_provider_turn_discard_callback_counts_interruption_out_of_order',
    'discard_callback_counts.interruption.malformed':
      'runtime_provider_turn_discard_callback_counts_interruption_malformed',
    'discard_callback_counts.interruption.unsupported':
      'runtime_provider_turn_discard_callback_counts_interruption_unsupported',
    'discard_callback_counts.alignment.duplicate':
      'runtime_provider_turn_discard_callback_counts_alignment_duplicate',
    'discard_callback_counts.alignment.late':
      'runtime_provider_turn_discard_callback_counts_alignment_late',
    'discard_callback_counts.alignment.empty':
      'runtime_provider_turn_discard_callback_counts_alignment_empty',
    'discard_callback_counts.alignment.out_of_order':
      'runtime_provider_turn_discard_callback_counts_alignment_out_of_order',
    'discard_callback_counts.alignment.malformed':
      'runtime_provider_turn_discard_callback_counts_alignment_malformed',
    'discard_callback_counts.alignment.unsupported':
      'runtime_provider_turn_discard_callback_counts_alignment_unsupported',
    'discard_callback_counts.livekit_data.duplicate':
      'runtime_provider_turn_discard_callback_counts_livekit_data_duplicate',
    'discard_callback_counts.livekit_data.late':
      'runtime_provider_turn_discard_callback_counts_livekit_data_late',
    'discard_callback_counts.livekit_data.empty':
      'runtime_provider_turn_discard_callback_counts_livekit_data_empty',
    'discard_callback_counts.livekit_data.out_of_order':
      'runtime_provider_turn_discard_callback_counts_livekit_data_out_of_order',
    'discard_callback_counts.livekit_data.malformed':
      'runtime_provider_turn_discard_callback_counts_livekit_data_malformed',
    'discard_callback_counts.livekit_data.unsupported':
      'runtime_provider_turn_discard_callback_counts_livekit_data_unsupported',
    discarded_callback_class: 'runtime_provider_turn_discarded_callback_class',
    discarded_callback_count: 'runtime_provider_turn_discarded_callback_count',
    preceding_callback_order_class: 'runtime_provider_turn_preceding_callback_order_class',
    adapter_turn_state: 'runtime_provider_turn_adapter_turn_state',
    discarded_callback_identity_relationship:
      'runtime_provider_turn_discarded_callback_identity_relationship',
    reconciliation_action: 'runtime_provider_turn_reconciliation_action',
    reconciliation_resolution: 'runtime_provider_turn_reconciliation_resolution',
    terminal_trigger: 'runtime_provider_turn_terminal_trigger',
  },
  turn_render: {
    turn_id: 'runtime_turn_id',
    message_id: 'runtime_message_id',
    started_mode: 'runtime_turn_render_started_mode',
    final_mode: 'runtime_turn_render_final_mode',
    mode_transition_count: 'runtime_turn_render_mode_transition_count',
    lifecycle: 'runtime_turn_render_lifecycle',
    segment_count: 'runtime_turn_render_segment_count',
    authoritative_character_count: 'runtime_turn_render_authoritative_character_count',
    revealed_character_count: 'runtime_turn_render_revealed_character_count',
    missing_character_count: 'runtime_turn_render_missing_character_count',
    authoritative_response_received: 'runtime_turn_render_authoritative_response_received',
    message_target_changed: 'runtime_turn_render_message_target_changed',
    final_reveal_complete: 'runtime_turn_render_final_reveal_complete',
  },
  turn_reconciliation: {
    turn_id: 'runtime_turn_id',
    message_id: 'runtime_message_id',
    terminal_authoritative_character_count:
      'runtime_turn_reconciliation_terminal_authoritative_character_count',
    terminal_revealed_character_count:
      'runtime_turn_reconciliation_terminal_revealed_character_count',
    terminal_missing_character_count:
      'runtime_turn_reconciliation_terminal_missing_character_count',
    terminal_authoritative_response_received:
      'runtime_turn_reconciliation_terminal_authoritative_response_received',
    terminal_alignment_character_count:
      'runtime_turn_reconciliation_terminal_alignment_character_count',
    grace_authoritative_character_count:
      'runtime_turn_reconciliation_grace_authoritative_character_count',
    grace_revealed_character_count: 'runtime_turn_reconciliation_grace_revealed_character_count',
    grace_missing_character_count: 'runtime_turn_reconciliation_grace_missing_character_count',
    grace_authoritative_response_received:
      'runtime_turn_reconciliation_grace_authoritative_response_received',
    grace_alignment_character_count: 'runtime_turn_reconciliation_grace_alignment_character_count',
    post_terminal_alignment_growth_character_count:
      'runtime_turn_reconciliation_post_terminal_alignment_growth_character_count',
    authoritative_response_delay_after_terminal_ms:
      'runtime_turn_reconciliation_authoritative_response_delay_after_terminal_ms',
    audio_playback_started: 'runtime_turn_reconciliation_audio_playback_started',
    related_tool_call_count: 'runtime_turn_reconciliation_related_tool_call_count',
    grace_duration_ms: 'runtime_turn_reconciliation_grace_duration_ms',
    outcome: 'runtime_turn_reconciliation_outcome',
    provider_callback_order_class: 'runtime_turn_reconciliation_provider_callback_order_class',
    discarded_callback_class: 'runtime_turn_reconciliation_discarded_callback_class',
    discarded_callback_count: 'runtime_turn_reconciliation_discarded_callback_count',
    preceding_callback_order_class: 'runtime_turn_reconciliation_preceding_callback_order_class',
    adapter_turn_state: 'runtime_turn_reconciliation_adapter_turn_state',
    discarded_callback_identity_relationship:
      'runtime_turn_reconciliation_discarded_callback_identity_relationship',
    reconciliation_action: 'runtime_turn_reconciliation_reconciliation_action',
    reconciliation_resolution: 'runtime_turn_reconciliation_reconciliation_resolution',
    terminal_trigger: 'runtime_turn_reconciliation_terminal_trigger',
    provider_generation_relationship:
      'runtime_turn_reconciliation_provider_generation_relationship',
    terminal_playback_authority_state:
      'runtime_turn_reconciliation_terminal_playback_authority_state',
    grace_playback_authority_state: 'runtime_turn_reconciliation_grace_playback_authority_state',
  },
  typewriter_drift: {
    turn_id: 'runtime_turn_id',
    native_progress_sample_count: 'runtime_typewriter_drift_native_progress_sample_count',
    native_timestamp_unavailable_count:
      'runtime_typewriter_drift_native_timestamp_unavailable_count',
    max_native_callback_delay_ms: 'runtime_typewriter_drift_max_native_callback_delay_ms',
    max_native_progress_age_ms: 'runtime_typewriter_drift_max_native_progress_age_ms',
    anchor_correction_count: 'runtime_typewriter_drift_anchor_correction_count',
    max_anchor_correction_ms: 'runtime_typewriter_drift_max_anchor_correction_ms',
    sample_count: 'runtime_typewriter_drift_sample_count',
    max_absolute_drift_ms: 'runtime_typewriter_drift_max_absolute_drift_ms',
    duration_outside_tolerance_ms: 'runtime_typewriter_drift_duration_outside_tolerance_ms',
    recovered_by_turn_end: 'runtime_typewriter_drift_recovered_by_turn_end',
    timing_source: 'runtime_typewriter_drift_timing_source',
    started_mode: 'runtime_typewriter_drift_started_mode',
    final_mode: 'runtime_typewriter_drift_final_mode',
    mode_transition_count: 'runtime_typewriter_drift_mode_transition_count',
    partial_word_reveal_prevention_count:
      'runtime_typewriter_drift_partial_word_reveal_prevention_count',
    max_word_reveal_lag_count: 'runtime_typewriter_drift_max_word_reveal_lag_count',
    word_boundary_remap_count: 'runtime_typewriter_drift_word_boundary_remap_count',
    dash_boundary_count: 'runtime_typewriter_drift_dash_boundary_count',
    max_word_reveal_burst_count: 'runtime_typewriter_drift_max_word_reveal_burst_count',
    tolerance_status: 'runtime_typewriter_drift_tolerance_status',
  },
  typewriter_ui_schedule: {
    turn_id: 'runtime_turn_id',
    policy_version: 'runtime_typewriter_ui_schedule_policy_version',
    timing_source: 'runtime_typewriter_ui_schedule_timing_source',
    text_source: 'runtime_typewriter_ui_schedule_text_source',
    dash_boundary_count: 'runtime_typewriter_ui_schedule_dash_boundary_count',
    schedule_contraction_count: 'runtime_typewriter_ui_schedule_schedule_contraction_count',
    ordinal_text_mutation_count: 'runtime_typewriter_ui_schedule_ordinal_text_mutation_count',
    visible_prefix_rewrite_attempt_count:
      'runtime_typewriter_ui_schedule_visible_prefix_rewrite_attempt_count',
    scheduled_word_count: 'runtime_typewriter_ui_schedule_scheduled_word_count',
    schedule_update_count: 'runtime_typewriter_ui_schedule_schedule_update_count',
    max_schedule_growth_word_count: 'runtime_typewriter_ui_schedule_max_schedule_growth_word_count',
    late_schedule_update_count: 'runtime_typewriter_ui_schedule_late_schedule_update_count',
    max_late_schedule_growth_word_count:
      'runtime_typewriter_ui_schedule_max_late_schedule_growth_word_count',
    max_schedule_availability_lag_ms:
      'runtime_typewriter_ui_schedule_max_schedule_availability_lag_ms',
    alignment_text_schedule_update_count:
      'runtime_typewriter_ui_schedule_alignment_text_schedule_update_count',
    max_alignment_scheduled_word_count:
      'runtime_typewriter_ui_schedule_max_alignment_scheduled_word_count',
    max_js_commit_to_next_frame_callback_ms:
      'runtime_typewriter_ui_schedule_max_js_commit_to_next_frame_callback_ms',
    max_ui_completion_to_js_callback_ms:
      'runtime_typewriter_ui_schedule_max_ui_completion_to_js_callback_ms',
    max_ui_observation_to_js_callback_ms:
      'runtime_typewriter_ui_schedule_max_ui_observation_to_js_callback_ms',
    ui_completion_ack_count: 'runtime_typewriter_ui_schedule_ui_completion_ack_count',
    ui_observation_count: 'runtime_typewriter_ui_schedule_ui_observation_count',
    max_ui_reveal_burst_word_count: 'runtime_typewriter_ui_schedule_max_ui_reveal_burst_word_count',
    max_ui_frame_gap_ms: 'runtime_typewriter_ui_schedule_max_ui_frame_gap_ms',
    ui_frame_gap_catch_up_count: 'runtime_typewriter_ui_schedule_ui_frame_gap_catch_up_count',
    max_ui_visible_playback_lag_ms: 'runtime_typewriter_ui_schedule_max_ui_visible_playback_lag_ms',
    follow_scroll_retarget_count: 'runtime_typewriter_ui_schedule_follow_scroll_retarget_count',
    follow_scroll_member_disable_count:
      'runtime_typewriter_ui_schedule_follow_scroll_member_disable_count',
    follow_scroll_member_enable_count:
      'runtime_typewriter_ui_schedule_follow_scroll_member_enable_count',
    max_follow_scroll_distance_from_bottom_while_enabled:
      'runtime_typewriter_ui_schedule_max_follow_scroll_distance_from_bottom_while_enabled',
    configured_fade_duration_ms: 'runtime_typewriter_ui_schedule_configured_fade_duration_ms',
    app_nonactive_episode_count: 'runtime_typewriter_ui_schedule_app_nonactive_episode_count',
    total_app_nonactive_duration_ms:
      'runtime_typewriter_ui_schedule_total_app_nonactive_duration_ms',
    max_app_nonactive_duration_ms: 'runtime_typewriter_ui_schedule_max_app_nonactive_duration_ms',
    animation_thread: 'runtime_typewriter_ui_schedule_animation_thread',
    render_surface: 'runtime_typewriter_ui_schedule_render_surface',
    word_fade_completed_count: 'runtime_typewriter_ui_schedule_word_fade_completed_count',
    word_static_committed_count: 'runtime_typewriter_ui_schedule_word_static_committed_count',
    word_fade_cancelled_count: 'runtime_typewriter_ui_schedule_word_fade_cancelled_count',
    word_opacity_regression_count: 'runtime_typewriter_ui_schedule_word_opacity_regression_count',
    word_remount_count: 'runtime_typewriter_ui_schedule_word_remount_count',
    outcome: 'runtime_typewriter_ui_schedule_outcome',
  },
  word_visibility: {
    turn_id: 'runtime_turn_id',
    phase: 'runtime_word_visibility_phase',
    word_index: 'runtime_word_visibility_word_index',
    progress: 'runtime_word_visibility_progress',
    elapsed_ms: 'runtime_word_visibility_elapsed_ms',
    schedule_version: 'runtime_word_visibility_schedule_version',
    render_surface: 'runtime_word_visibility_render_surface',
  },
  text_send: {
    turn_id: 'runtime_turn_id',
    message_id: 'runtime_message_id',
    connection_state_at_submit: 'runtime_text_send_connection_state_at_submit',
    delivery_status: 'runtime_text_send_delivery_status',
    attempt_count: 'runtime_text_send_attempt_count',
    pending_duration_ms: 'runtime_text_send_pending_duration_ms',
    retry_result: 'runtime_text_send_retry_result',
    contextual_update_result: 'runtime_text_send_contextual_update_result',
    qualified_typed_interruption: 'runtime_text_send_qualified_typed_interruption',
    contextual_update_attempted: 'runtime_text_send_contextual_update_attempted',
  },
  tool_call: {
    tool_call_id: 'runtime_tool_call_id',
    tool_name: 'runtime_tool_call_tool_name',
    status: 'runtime_tool_call_status',
    tool_call_id_source: 'runtime_tool_call_tool_call_id_source',
    tool_class: 'runtime_tool_call_tool_class',
    persistence_path: 'runtime_tool_call_persistence_path',
    received_mode: 'runtime_tool_call_received_mode',
    presentation_target: 'runtime_tool_call_presentation_target',
    anchor_turn_id: 'runtime_anchor_turn_id',
    delivery_count: 'runtime_tool_call_delivery_count',
    effect_execution_count: 'runtime_tool_call_effect_execution_count',
    anchored_to_turn: 'runtime_tool_call_anchored_to_turn',
    stale_result_rejected: 'runtime_tool_call_stale_result_rejected',
    mutation_cache_result: 'runtime_tool_call_mutation_cache_result',
    projection_matched: 'runtime_tool_call_projection_matched',
  },
  tool_replay: {
    tool_call_id: 'runtime_tool_call_id',
    tool_name: 'runtime_tool_replay_tool_name',
    tool_call_id_source: 'runtime_tool_replay_tool_call_id_source',
    received_mode: 'runtime_tool_replay_received_mode',
    replay_timing: 'runtime_tool_replay_replay_timing',
    delivery_count: 'runtime_tool_replay_delivery_count',
    effect_execution_count: 'runtime_tool_replay_effect_execution_count',
  },
  generation_rejection: {
    event_type: 'runtime_generation_rejection_event_type',
    event_source: 'runtime_generation_rejection_event_source',
    event_generation: 'runtime_generation_rejection_event_generation',
    active_generation: 'runtime_generation_rejection_active_generation',
    anomaly_code: 'runtime_generation_rejection_anomaly_code',
    rejected_class: 'runtime_generation_rejection_rejected_class',
    runtime_identity_mismatch: 'runtime_generation_rejection_runtime_identity_mismatch',
    teardown_age_ms: 'runtime_generation_rejection_teardown_age_ms',
  },
  session_lifecycle: {
    outcome: 'runtime_session_lifecycle_outcome',
    guard_layer: 'runtime_session_lifecycle_guard_layer',
    provider_start_generation: 'runtime_session_lifecycle_provider_start_generation',
    active_provider_start_generation: 'runtime_session_lifecycle_active_provider_start_generation',
    lifecycle_state: 'runtime_session_lifecycle_lifecycle_state',
    callback_class: 'runtime_session_lifecycle_callback_class',
    runtime_identity_mismatch: 'runtime_session_lifecycle_runtime_identity_mismatch',
    teardown_age_ms: 'runtime_session_lifecycle_teardown_age_ms',
    provider_event_type: 'runtime_session_lifecycle_provider_event_type',
    attempt_number: 'runtime_session_lifecycle_attempt_number',
    close_source: 'runtime_session_lifecycle_close_source',
    startup_stage: 'runtime_session_lifecycle_startup_stage',
    startup_generation: 'runtime_session_lifecycle_startup_generation',
    active_startup_generation: 'runtime_session_lifecycle_active_startup_generation',
  },
  background_episode: {
    schema_version: 'runtime_schema_version',
    attempt_id: 'runtime_attempt_id',
    episode_id: 'runtime_episode_id',
    service_instance_id: 'runtime_service_instance_id',
    platform: 'runtime_background_episode_platform',
    lifecycle_cause: 'runtime_background_episode_lifecycle_cause',
    service_start_state: 'runtime_background_episode_service_start_state',
    service_failure_class: 'runtime_background_episode_service_failure_class',
    notification_permission_granted: 'runtime_background_episode_notification_permission_granted',
    background_duration_ms: 'runtime_background_episode_background_duration_ms',
    disconnect_observed_at: 'runtime_background_episode_disconnect_observed_at',
    disconnect_observed_after_background_ms:
      'runtime_background_episode_disconnect_observed_after_background_ms',
    connected_at_entry: 'runtime_background_episode_connected_at_entry',
    connected_at_return: 'runtime_background_episode_connected_at_return',
    microphone_input_observed: 'runtime_background_episode_microphone_input_observed',
    agent_output_observed: 'runtime_background_episode_agent_output_observed',
    microphone_input_observed_in_background:
      'runtime_background_episode_microphone_input_observed_in_background',
    agent_output_observed_in_background:
      'runtime_background_episode_agent_output_observed_in_background',
    provider_response_observed_in_background:
      'runtime_background_episode_provider_response_observed_in_background',
    last_provider_response_after_background_ms:
      'runtime_background_episode_last_provider_response_after_background_ms',
    first_usable_response_after_return:
      'runtime_background_episode_first_usable_response_after_return',
    recovery_attempted: 'runtime_background_episode_recovery_attempted',
    recovery_resolved: 'runtime_background_episode_recovery_resolved',
    fallback_attempted: 'runtime_background_episode_fallback_attempted',
    battery_optimization_class: 'runtime_background_episode_battery_optimization_class',
    outcome: 'runtime_background_episode_outcome',
  },
  caption_visibility_anomaly: {
    turn_id: 'runtime_turn_id',
    final_mode: 'runtime_turn_render_final_mode',
    lifecycle: 'runtime_turn_render_lifecycle',
    boundary: 'runtime_caption_visibility_boundary',
    reason: 'runtime_caption_visibility_reason',
    observation_source: 'runtime_caption_visibility_observation_source',
    sequence: 'runtime_caption_visibility_sequence',
    elapsed_ms: 'runtime_caption_visibility_elapsed_ms',
    prefix_before_count: 'runtime_caption_visibility_prefix_before_count',
    prefix_after_count: 'runtime_caption_visibility_prefix_after_count',
    selected_target_changed: 'runtime_caption_visibility_selected_target_changed',
    projection_present: 'runtime_caption_visibility_projection_present',
    renderer_generation: 'runtime_caption_visibility_renderer_generation',
    coverage: 'runtime_caption_visibility_coverage',
  },
  caption_visibility_settled: {
    turn_id: 'runtime_turn_id',
    final_mode: 'runtime_turn_render_final_mode',
    lifecycle: 'runtime_turn_render_lifecycle',
    projection_revision: 'runtime_caption_visibility_projection_revision',
    renderer_generation: 'runtime_caption_visibility_renderer_generation',
    expected_transition_count: 'runtime_caption_visibility_expected_transition_count',
    unexpected_transition_count: 'runtime_caption_visibility_unexpected_transition_count',
    candidate_rewrite_count: 'runtime_caption_visibility_candidate_rewrite_count',
    observed_regression_count: 'runtime_caption_visibility_observed_regression_count',
    monitor_duration_ms: 'runtime_caption_visibility_monitor_duration_ms',
    coverage: 'runtime_caption_visibility_coverage',
    outcome: 'runtime_caption_visibility_outcome',
  },
} as const;

export type RuntimeTelemetryFamily = keyof typeof RUNTIME_FIELD_MAP;
export type RuntimeAttributeKey = keyof typeof RUNTIME_ATTRIBUTE_RULES;
export type RuntimeAttributeValue<Key extends RuntimeAttributeKey> =
  (typeof RUNTIME_ATTRIBUTE_RULES)[Key] extends { kind: 'enum'; values: readonly (infer Value)[] }
    ? Value
    : (typeof RUNTIME_ATTRIBUTE_RULES)[Key] extends { kind: 'boolean' }
      ? boolean
      : (typeof RUNTIME_ATTRIBUTE_RULES)[Key] extends { kind: 'uuid' | 'version' | 'build' }
        ? string
        : (typeof RUNTIME_ATTRIBUTE_RULES)[Key] extends { kind: 'schema' }
          ? 1
          : number;
export type RuntimeAvailabilityKey = `${RuntimeAttributeKey}_availability`;
export const RUNTIME_AVAILABILITY_VALUES = ['measured', 'unavailable', 'not_applicable'] as const;

const runtimeRules = new Map<string, RuntimeAttributeRule>(Object.entries(RUNTIME_ATTRIBUTE_RULES));
const runtimeAvailabilityKeys = new Set(
  Object.keys(RUNTIME_ATTRIBUTE_RULES).map((key) => `${key}_availability`),
);

/** Character classes a failed summary parse reports around its failure offset. */
const SUMMARY_PARSE_CHAR_CLASSES = [
  'letter',
  'digit',
  'space',
  'newline',
  'tab',
  'other_control',
  'double_quote',
  'single_quote',
  'smart_quote',
  'backslash',
  'brace_open',
  'brace_close',
  'bracket_open',
  'bracket_close',
  'comma',
  'colon',
  'other_punct',
  'non_ascii',
  'none',
] as const;

/**
 * Closed vocabularies for registered attributes outside the conversation
 * runtime. Kept apart from RUNTIME_ATTRIBUTE_RULES because every key there also
 * gets a `<key>_availability` companion stamped onto each mobile runtime event.
 */
export const ENUM_ATTRIBUTE_VALUES = {
  // How a clinician resolved an escalation: a mirror of ESCALATION_DISPOSITION
  // (`@galapagos/shared`, which this package cannot import), pinned by the
  // mark-handled service test in apps/api. Never the free-text resolution detail.
  escalation_disposition: [
    'contacted_member_safe',
    'brief_safety_assessment_completed',
    'emergency_evaluation_initiated',
    'referred_988',
    'false_positive',
    'other',
  ],
  // A failed Change Healthcare request, reduced to what it looked like and never
  // what it said: a CH body can echo the request's credentials, hsi value,
  // demographics or drug, so no byte of it is logged. Mirrors of the CH_*
  // tuples in apps/api's change-healthcare types; a test there checks every
  // tuple value is admitted here, so a value added there and not here fails.
  ch_content_type: ['xml', 'html', 'json', 'text', 'other', 'absent'],
  ch_response_shape: ['empty', 'xml_error', 'xml_result', 'xml_other', 'html', 'json', 'text'],
  ch_transport_cause: ['timeout', 'dns', 'refused', 'reset', 'tls', 'socket', 'other'],
  ch_transport_stage: ['request', 'body'],
  // The CH XML action a failure or circuit event names, a mirror of CH_ACTIONS
  // (pinned by the same apps/api test). Namespaced because the bare `action`
  // must stay denied.
  ch_action: [
    'hsilabel_search',
    'person_put_partial',
    'person_search_hsi',
    'security_logout',
    'organization_get',
    'rx_search_org',
    'rx_search_patient_history',
  ],
  // The Change Healthcare prescription date a parse failure names (never the
  // date text), and which prescription-sync entry point read it. Closed
  // vocabularies fixed by the sync code; mirrors of CH_DATE_FIELDS and
  // RX_SYNC_SOURCES in apps/api, pinned by a test there.
  ch_date_field: ['creation_date', 'modified_date', 'thru_date'],
  rx_sync_source: ['sweep', 'backfill', 'reconcile'],
  evaluation_error_detail: [
    'timeout',
    'transport_error',
    'quota_limited',
    'unhealthy_response',
    'unconfigured',
    'redacted',
  ],
  /** Telemedicine member heartbeat access log — closed phase enum, not PII. */
  member_call_phase: ['waiting_room', 'in_call'],
  // Module progress: mirrors of MODULE_KEYS and MODULE_COMPLETION_SOURCE
  // (`@galapagos/shared`), pinned by the module-progress telemetry test in
  // apps/api, so a log can only carry a catalogued module and a known source.
  module_key: ['essentials-1'],
  module_completion_source: ['app', 'legacy_1_0', 'staff_recorded'],
  // Which producer files a voice-launch notification open: the client (the mint
  // announced it holds the record) or the server (every other mint).
  recorded_by: ['client', 'server'],
  // The notification-open ledger's local fallback when storage fails.
  fallback: ['treat_as_unrecorded', 'skip_write'],
  // What happened to a degraded journey slot on its way to the default
  // technique: a mirror of JOURNEY_FALLBACK_DETAILS (`@galapagos/shared`),
  // pinned by the composer telemetry test in apps/api. Journey-qualified so no
  // other producer inherits the key.
  journey_fallback_detail: ['no_intent', 'intent_duplicate', 'copy_rewrite_failed', 'gate_dropped'],
  // Healthie reconcile run summary: the stream and its closed run outcome.
  reconcileStream: ['forms', 'signatures'],
  reconcileOutcome: [
    'completed',
    'truncated',
    'healthie_unavailable',
    'unconfigured',
    'invalid_payload',
  ],
  // Why the reconcile last failed on a retry-table row.
  retryErrorClass: [
    'healthie_transient',
    'healthie_unavailable',
    'in_progress',
    'other',
    'not_retryable',
    'deferred',
  ],
  // A summary completion that did not parse as JSON, reduced to its shape:
  // mirrors of JSON_PARSE_ERROR_KINDS, JSON_POSITION_SOURCES, JSON_CHAR_CLASSES
  // and ATTEMPT_TURNS (`@galapagos/personalisation`, which this package cannot
  // import), pinned by the conversation-summaries attribute test in apps/api.
  // Character classes, never characters; the completion holds member content.
  summary_parse_error_kind: [
    'unexpected_token',
    'unexpected_end',
    'unterminated_string',
    'bad_control_character',
    'bad_escape',
    'trailing_content',
    'expected_property_name',
    'expected_comma_or_brace',
    'expected_comma_or_bracket',
    'expected_colon',
    'bad_number',
    'other',
  ],
  summary_parse_position_source: ['reported', 'scanned', 'none'],
  summary_parse_turn: ['first', 'retry', 'repair'],
  summary_parse_char_before_3: SUMMARY_PARSE_CHAR_CLASSES,
  summary_parse_char_before_2: SUMMARY_PARSE_CHAR_CLASSES,
  summary_parse_char_before_1: SUMMARY_PARSE_CHAR_CLASSES,
  summary_parse_char_at: SUMMARY_PARSE_CHAR_CLASSES,
  summary_parse_char_after_1: SUMMARY_PARSE_CHAR_CLASSES,
  summary_parse_char_after_2: SUMMARY_PARSE_CHAR_CLASSES,
  summary_parse_char_after_3: SUMMARY_PARSE_CHAR_CLASSES,
  // How reading a refused reward send's body went, never the body itself: a
  // mirror of SENDOSO_BODY_READ_OUTCOMES in apps/api's Sendoso telemetry,
  // pinned by the Sendoso provider test there, so a value added on one side
  // and not the other fails.
  body_read: ['text', 'no_text', 'timeout', 'error'],
  // Why a reward order's last vendor send failed, as the order row records it,
  // or `none` when it has no failure: a mirror of REWARD_SEND_FAILURES in
  // packages/shared, pinned by the fulfilment telemetry test in apps/api, so a
  // value added on one side and not the other fails.
  last_send_failure: [
    'insufficient_funds',
    'unauthorised',
    'rate_limited',
    'vendor_error',
    'unrecognised',
    'unavailable',
    'timeout',
    'connection_error',
    'no_send_ref',
    'token_unavailable',
    'pre_send_invalid',
    'unknown',
    'none',
  ],
} as const satisfies Record<string, readonly string[]>;
/** The journey fallback-detail vocabulary the filter admits, for producers to pin their mirror against. */
export const SAFE_JOURNEY_FALLBACK_DETAIL_VALUES: readonly string[] =
  ENUM_ATTRIBUTE_VALUES.journey_fallback_detail;
/** The body-read outcomes the filter admits under `body_read`, for producers to pin their mirror against. */
export const SAFE_BODY_READ_VALUES: readonly string[] = ENUM_ATTRIBUTE_VALUES.body_read;
/** The send-failure values the filter admits under `last_send_failure`, for producers to pin their mirror against. */
export const SAFE_REWARD_SEND_FAILURE_VALUES: readonly string[] =
  ENUM_ATTRIBUTE_VALUES.last_send_failure;

const enumAttributeValues = new Map<string, readonly string[]>(
  Object.entries(ENUM_ATTRIBUTE_VALUES),
);

/** Fail closed at every operational provider boundary, even for registered runtime keys. */
export function isSafeRuntimeAttribute(
  key: string,
  value: unknown,
): value is string | number | boolean {
  if (runtimeAvailabilityKeys.has(key))
    return value === 'measured' || value === 'unavailable' || value === 'not_applicable';
  const rule = runtimeRules.get(key);
  if (!rule) return false;
  switch (rule.kind) {
    case 'enum':
      return typeof value === 'string' && rule.values.includes(value);
    case 'boolean':
      return typeof value === 'boolean';
    case 'schema':
      return value === 1;
    case 'integer':
      return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
    case 'number':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= 0 &&
        value <= Number.MAX_SAFE_INTEGER
      );
    case 'uuid':
      return (
        typeof value === 'string' &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
      );
    case 'version':
      return (
        typeof value === 'string' && value.length <= 40 && /^[0-9]+(?:\.[0-9]+){0,3}$/.test(value)
      );
    case 'build':
      return typeof value === 'string' && /^[0-9]{1,12}$/.test(value);
  }
}

/**
 * Curated operational-telemetry attribute vocabulary (ADR-069). This registry
 * lives below `@galapagos/telemetry` so vendor providers can enforce the same
 * default-deny boundary without creating a package dependency cycle;
 * `@galapagos/telemetry` re-exports it as the single source of truth. A
 * `/spec`-declared new safe field is added here — never to a provider-local
 * copy, which is exactly the drift this shared set exists to prevent.
 */
export const SAFE_ATTR_KEYS = new Set<string>([
  ...runtimeRules.keys(),
  ...runtimeAvailabilityKeys,
  ...enumAttributeValues.keys(),
  'event',
  'status_class',
  // A client-parsed vendor response carried a value outside the vocabulary the
  // schema declares. `vendor_enum_field` is the schema field path, authored in
  // code and never derived from the wire. `vendor_enum_value` is the offending
  // token, admitted only when it already matches an enum-token shape
  // (`^[A-Za-z0-9_.-]{1,40}$`) — anything else is reported as a placeholder, so
  // free wire text cannot reach the vendor through this key.
  // `vendor_enum_reason` separates an unrecognised value from an absent field,
  // which would otherwise be indistinguishable in the same signal.
  // The bound is `boundVendorEnumValue` below — it is exported beside this
  // entry deliberately, so the constraint that licenses the key cannot drift
  // away from it into a single call site.
  'vendor_enum_field',
  'vendor_enum_value',
  'vendor_enum_reason',
  'result',
  // Which estate a member-access justification was propagated to: the closed
  // `to_pelago1 | to_pelago2` pair, never a member, reference or reason.
  'direction',
  // Which producer files a voice-launch notification open (client | server).
  'recorded_by',
  // What a read boundary did instead after an unexpected failure (closed enum).
  'fallback',
  // Closed push-control catalogue, deployment target and bounded evaluation age.
  'definition_key',
  'flag_key',
  'deployment_environment_key',
  'flag_source',
  'snapshot_age_ms',
  'method',
  'route',
  // A structural JSON-pointer state path (e.g. `/screen/substance`) — never an answer value.
  'path',
  'entry_id',
  'request_attempt_id',
  'diagnostic_stage',
  'metadata_cache_state',
  'history_cache_state',
  'observation_offset_ms',
  'request_purpose',
  'correlation_available',
  'duration_ms',
  // Closed navigation source for interaction-to-interactive measurements.
  'entry_point',
  'step_key',
  // Module progress (CBT modules): the catalogue module key, a member's step
  // write being new or a repeat, where a completion came from, and the 1.0
  // import's row counts. Closed vocabularies and integers only — never the
  // day, a note, a void reason or an id.
  'module_key',
  'was_new',
  'module_completion_source',
  'completions_written',
  'steps_written',
  'steps_unmapped',
  'prior_revision_rows_skipped',
  'service',
  'env',
  'version',
  'http_status',
  // Whether a GraphQL vendor answered with an `errors[]` envelope; a boolean,
  // never the error text.
  'graphql_error',
  'error_code',
  'close_code',
  'count',
  'ok',
  'outcome',
  'transition',
  // A counsellor's per-call AI notes decision (`on` | `off`) and whether it
  // replaced an earlier decision for the same call — closed enum and boolean.
  'decision',
  'correction',
  // Why an operation was refused — a closed set of snake_case reason tokens the
  // emitting handler chooses, never free text and never a caught error message.
  // Already emitted on the internal-pool audit path before it was registered.
  'failure_reason',
  // Sizes of a staff capability-group delta: how many groups a single change
  // granted and revoked. Bounded by the governed INTERNAL_GROUP catalogue, and
  // counts only — the group NAMES stay on the compliance audit event, which has
  // a considered retention and access posture; an operational chart needs the
  // magnitude, not who got what.
  'granted_count',
  'revoked_count',
  // Signed points difference between an imported reward ledger and its legacy
  // reference balance: a bounded integer, never member data.
  'delta_points',
  // Staff org role on the member-scope observation log — the closed 9-value
  // INTERNAL_ROLE vocabulary, bounded by the internal_users role CHECK. The
  // log-side twin of the `staff_role` metric tag: the two registries are
  // separate, so admitting the dimension for metrics says nothing about the
  // attribute, and an unregistered key is dropped by `filterSafeAttributes`.
  'staff_role',
  // On what footing a staff caller was admitted to an adhoc PSTN call — the
  // closed four-value CARE_ADHOC_CALL_ACCESS_BASES vocabulary (bounded by the
  // care_adhoc_calls CHECK). Its own key rather than `access_basis`: that one
  // carries the member-record access audit's different four values, and one
  // key over two vocabularies is the bare-noun ambiguity this file avoids.
  'call_access_basis',
  // Initial-review operational classifications (spec-declared): a fixed flow
  // version plus bounded state/count buckets. These never contain identities,
  // dates, program data, answers, or free-form text.
  'plan_kind',
  // Home care-section resolution (spec-declared): closed state id and whether
  // a fact read forced develop's fallback rendering — never member content.
  'care_section_state',
  'care_section_fallback',
  'source_plan_kind',
  'successor_plan_kind',
  'selected_theme_count',
  'eligibility_reason',
  'email_match',
  'review_state',
  'day_count_bucket',
  // Weekly-plan review-context read dimensions (spec-declared): the closed
  // surface / verb / row-kind vocabularies the bucketed structural warns carry,
  // the one-or-multiple count bucket, and the numeric row bound a truncated
  // read reports. None can carry member content, dates, or program data.
  'verb',
  'row_kind',
  'count_bucket',
  'limit',
  // Weekly-plan theme generation dimensions (spec-declared): the Bedrock model
  // identifier and prompt version that produced a set, how many themes it held,
  // presence booleans for each evidence feed, and how many earlier-app plans
  // were seeded (0–3). Counts, flags and build identifiers only — never theme
  // copy, memories, conversation text, or plan content.
  'model_id',
  'prompt_version',
  'suggestion_count',
  'memory_present',
  'conversation_present',
  'plan_history_present',
  'legacy_plans_seeded',
  // Lifecycle stage of a bounded operation (e.g. a session completion's
  // disconnect vs complete step) — always a closed enum, never content.
  'stage',
  'operation',
  // Care-chat attachment lifecycle dimensions (spec-declared). The
  // conversation id is an opaque server UUID, never member-authored content;
  // the remaining fields are closed media/actor enums or a bounded byte count.
  'conversationId',
  'participantType',
  'mediaType',
  'sizeBytes',
  // Typed-turn size guard: byte counts of a member's typed turn and of the
  // quoted previous agent reply, never the text itself.
  'member_bytes',
  'limit_bytes',
  'context_bytes',
  'kept_context_bytes',
  // Conversation-realtime transport dimensions (spec-declared): closed event,
  // failure, and retry vocabularies only. They never carry message content,
  // participant identity, provider response text, or provider request ids.
  'event_kind',
  'failure_class',
  'retryable',
  // Device push platform — the closed ios/android pair (PUSH_PLATFORM), so a
  // degraded native-token read can be counted per platform. Never a device id.
  'platform',
  'attempt_band',
  // Version of a local persisted-record schema — a bounded integer, never
  // member-authored plan content or identity.
  'storage_version',
  // Migration gap diagnostics carry presence flags and a count, never answers.
  'has_program',
  'has_goal',
  'has_motivation',
  'fields_missing_count',
  // Migration data quality: where each intake answer came from (closed source
  // enum), what Pelago 1.0 held (booleans), how the primary program was chosen
  // (closed rule enum, and whether it matches 1.0), program counts either side
  // of a post-migration addition with its closed source enum, and how many gap
  // fields the member answered. Never a program key, domain or answer text —
  // those name a substance and stay on the BAA-covered analytics plane.
  'program_source',
  'goal_source',
  'motivation_source',
  'has_card',
  'has_complete_card',
  'enrolled_program_count',
  'primary_rule',
  'primary_matches_legacy',
  'enrollment_source',
  'program_count_before',
  'program_count_after',
  'fields_collected',
  'has_schedule',
  'has_destination',
  'enabled',
  'logsEnabled',
  'startupStatus',
  'chunkCount',
  'wordCount',
  'durationMs',
  'driftMs',
  // Milliseconds between the conversation UI going live and the full connect
  // chain settling — a bounded duration sample, never content.
  'chainLagMs',
  // Greeting-watchdog durations: the configured deadline and the measured
  // wall-clock elapsed (which can exceed the deadline after backgrounding) —
  // bounded duration samples, never content.
  'deadlineMs',
  'elapsedMs',
  'hasProviderTiming',
  'textLength',
  'playbackElapsedMs',
  'alignedDurationMs',
  'turnSequence',
  'diagnosticSequence',
  'alignmentWaitMs',
  'textBufferedMs',
  'duplicateReason',
  'releaseReason',
  'disconnectSource',
  'releaseDisposition',
  'playbackRemainingMs',
  'providerMode',
  'phase',
  'terminalStatus',
  'handlerStatus',
  'settlementCause',
  'invocationGeneration',
  'optionCount',
  'verificationSessionId',
  'screenId',
  'agentDefinitionId',
  'agentFlowId',
  'callId',
  'correlationSource',
  'toolName',
  'speechActive',
  'overdueWordCount',
  'maximumOverdueMs',
  'catchupDurationMs',
  'toolActive',
  'timelineStage',
  'stageStatus',
  'absenceReason',
  // Voice caption timing/drift (non-reserved replacements for the Datadog-reserved
  // `status`/`source`, plus the correlation id + on-screen-vs-audio drift sample).
  'alignmentSource',
  'alignmentStatus',
  'eventId',
  'toolEventId',
  'audioEventId',
  'anchorSource',
  'clientPlatform',
  'alignmentToPlayoutMs',
  'anchorWaitMs',
  'playoutFallbackReason',
  // Reveal-clock starvation sample: the turn's playout anchor (a relative
  // wallclock onset, not an identifier) and how long its native progress feed
  // had been silent when the wallclock fallback took over.
  'startedAtMs',
  'stallMs',
  'playoutDurationMs',
  'completionWaitMs',
  'wordIndex',
  'expectedMs',
  'revealMs',
  'sampled',
  // Member-transcript-to-agent-audio-anchor turn-around latency.
  'latencyMs',
  'reason',
  // The app version a client reported about ITSELF on a capability-gate
  // refusal. Bounded by construction rather than by trust: the resolver that
  // produces it carries the value only on the arms where it has already parsed
  // as a numeric `major.minor.patch`, and the arm holding an unvalidated header
  // has no such field at all — so what reaches here is three integers and two
  // dots, never the raw client string. It is the member's BUILD, not the
  // member: two devices on the same release are indistinguishable by it.
  //
  // Registered because a gate that withholds a control is invisible without it:
  // `reason` separates "old builds, as designed" from "nobody is reporting any
  // more", and this separates "one stale release" from "every release", which is
  // the difference between waiting and acting.
  'reported_version',
  // Whether a set-once write performed its transition on this call (true) or
  // found the value already set (false). A boolean about the write, never
  // about the member.
  'transitioned',
  // Staff-notification derivation outcomes. `notification_kind` is the closed
  // STAFF_NOTIFICATION_KIND vocabulary (a storage enum, never member data) —
  // qualified rather than a bare `kind`, so the grant in this repo-wide
  // default-deny list stays as narrow as the reasoning that justifies it and
  // does not admit any future emitter's unrelated `kind` — and
  // `author_role` is the closed MESSAGE_ROLE vocabulary — the value that tells
  // "a care person replied" from "the system wrote to a care conversation",
  // which is the distinction the skip signal exists to make. Both are bounded
  // enums, so neither raises metric cardinality (ADR-069 Rule 4).
  'notification_kind',
  'author_role',
  // Remote experiment resolution: the repo-registered experiment key (an
  // authored constant, never member data) and the reason class for degraded or
  // skipped resolutions — always a member-data-free value from the shared
  // RemoteAssignmentReasonClass closed vocabulary (the console-outage
  // visibility signal).
  'experiment_key',
  'reason_class',
  // Which author produced a rendered GenUI document: 'server' | 'bundled'. A
  // closed two-value enum carrying no member data — it exists so a dark launch
  // can compare the two authors on real traffic.
  'document_source',
  // The GenUI surface a signal is about, and the locale it was composed for.
  // Both are authored constants — a registered surface id and a negotiated
  // catalog locale — never member data.
  'surface_id',
  'locale',
  // Which link of the home-plan resolution chain served a request
  // ('member' | 'global' | 'default'), and the `source` column of the row that
  // did ('static' | 'rules' | 'llm'). Two closed enums; neither identifies a
  // member, and `resolution` is what tells an operator whether a seeded plan row
  // is actually being read.
  'resolution',
  'plan_source',
  // The plan contract version a row was read at, and how many sections the
  // resolved plan placed. An authored integer and a bounded small count — a
  // section count is not a content fingerprint.
  'plan_schema_version',
  'section_count',
  // Which sections a migrated member's Sona context document could not fill,
  // as a sorted comma-joined subset of a five-value closed vocabulary, and the
  // document's length in code points. The section codes name the estate's own
  // field groups (quit_date, programmes, goals, care_specialist, check_ins),
  // never a member's values, and a length is a scalar, not a fingerprint.
  'absent_sections',
  'document_chars',
  // Which portions a migration continuity summary composed, as a comma-joined
  // subset of the closed four-value response vocabulary (focus, goal,
  // check_ins, care_specialist — the summary schema's own keys). Portion NAMES
  // only, a bounded closed set — never a member's programme, goal text, day
  // count, or specialist name.
  'portions',
  // How a served migration-summary goal label was produced — 'mapped' (a known
  // 1.0 token resolved to catalog copy) or 'passthrough' (capitalised stored
  // text). A closed two-value enum; never the goal text itself.
  'goal_display',
  // Which field of a stored plan the contract rejected, as a Zod path over closed
  // vocabularies (or the bare column name for a root-level rejection). It names
  // schema keys, never their values.
  'rejected_path',
  // How many tasks one authored queue had to drop, beside the paths that
  // rejected them. A bounded count; never a task id or its copy.
  'dropped_count',
  // How many practices the home's completed band resolved for the member-day —
  // a bounded small count (one row per composed practice by constraint), never
  // a title or any other tile copy.
  'completed_count',
  // The home's journey-tile resolution and replacement-pending signals: how
  // many ordered ids rendered, were dropped, or fell past the render cap; whether
  // a replacement composition is owed and whether that promise aged past its TTL;
  // and the age of the newest completion in seconds. Bounded counts, booleans,
  // and a duration — never a practice id, a title, or any tile copy.
  'tile_count',
  'dropped_count',
  'excess_count',
  'pending',
  'expired',
  'age_seconds',
  // Home task-queue authored event dimensions: the leading task's closed kind
  // enum ('appointment' | 'weekly-review' | 'check-in' | 'focus-sentence') and
  // the two bounded queue sizes. The 'none' value is expected-unreachable
  // rather than retired: a standing kind is admitted in every member state, so
  // the queue normally has a leader, but the author keeps its fallback. A
  // 'none' data point therefore signals that admission dropped every task —
  // a validation failure worth seeing, not an impossible state.
  // Counts and a closed vocabulary only — never a task id, its copy, or any
  // other member data.
  'leading_kind',
  'todo_count',
  'done_count',
  // Journey outcome decision attributes: `source_tier` / `generation_reason`
  // are the closed journey vocabularies; `member_turns` is a bounded small
  // count of member-authored turns in one conversation (never content);
  // `floor` is the configured turn threshold (an authored integer); `day_key`
  // is a calendar day string, ADR-069-safe (no member id rides with it);
  // `cause` is a closed failure-class enum on the unresolvable-practice log.
  'source_tier',
  'generation_reason',
  'member_turns',
  'floor',
  'day_key',
  'cause',
  // Cost of adopting an arrived GenUI document — schema parse + catalog walk.
  // A duration sample, never content.
  'adopt_ms',
  // Time a Home document read joined a shared summary request before transport.
  // Bounded diagnostic duration, never member data.
  'coordination_wait_ms',
  // Unknown-GenUI-element fleet exposure (spec-declared): the catalog kind this
  // client could not render (an authored component name, never content) and the
  // app semver of the build that met it. Together with `resolution`,
  // `surface_id`/`flow_id`/`step_key`, `runtime_version`, and `update_id`
  // (already registered) they separate transient OTA lag from stranded runtimes
  // per kind.
  'element_kind',
  'app_version',
  // Build-owned migration probe correlation, never legacy payload values.
  'app_identity',
  'app_build',
  'probe_environment',
  // The same fleet-exposure plane for an unresolvable TOOL rather than an
  // unresolvable element kind: the catalogued tool name a served binding asked
  // for that this build's ToolCatalog does not carry. Server-authored and drawn
  // from a closed vocabulary — it names a product capability, never a member or
  // anything they entered. Singular, and distinct from the already-registered
  // `tool_names` (the comma-joined per-call voice summary list): this one is one
  // unknown name per resolution, and merging the two would put a fleet-exposure
  // count in the same dimension as a voice-call inventory.
  'tool_name',
  // Inclusive width, in days, of the date window a read asked for. A bounded
  // integer derived from an authored range — it says how much history was
  // requested, never which days a member has. It exists so one endpoint's two
  // query shapes (a week strip, months of history) do not land in one latency
  // distribution.
  'range_days',
  'series_key',
  'counsellor_key',
  // Care-appointment lifecycle dimensions (spec-declared): a bounded duration,
  // closed enums for the booking origin, the governed clinical role, who
  // cancelled or moved the call and how it completed, plus two booleans. The
  // start instant deliberately has no key here — a member-related date never
  // rides operational telemetry.
  'duration_minutes',
  'booking_source',
  'specialist_role',
  'cancelled_by',
  'rescheduled_by',
  'specialist_changed',
  'duration_changed',
  'is_first_appointment',
  'completed_via',
  // Booking integrity: what a key-replayed booking found (a closed status
  // enum) and which overlap constraint refused a write (`specialist`/`member`).
  'replay_status',
  'overlap_scope',
  'surface',
  // A check-in context-group key that fell back to the generic card heading.
  // Emitters pass it through `telemetryGroupKey`, which reports any key the
  // catalog does not name (`checkin.contextGroup_<group>_label`) as `invalid`, so
  // a key a member wrote never reaches a log verbatim.
  'context_group',
  // Telemedicine call diagnostics: the session id is an opaque server UUID for
  // log correlation only. Emitters validate it before use; it is never a metric tag.
  'callSessionId',
  'providerStatusClass',
  'provider',
  'per_specialist',
  // Care-team reassignment on a welcome booking (spec-declared): the closed
  // routable call-type enum and whether the default specialist stayed on the
  // team — no member, staff or assignment identity.
  'call_type',
  'modality',
  'previous_modality',
  'new_modality',
  'contact_type_label',
  'healthie_appointment_id',
  // Staff slot-read telemetry: bounded pool-read scalars only.
  'read_scope',
  'slot_count',
  'pool_size',
  'licence_filtered',
  // Acuity-assessment write events: which path wrote the row.
  'write_source',
  'cache_hits',
  'cache_misses',
  'default_retained',
  // Memory-extraction event attributes (spec-declared): the opaque provider
  // conversation id + pipeline outcome counts — scalars, never content.
  'conversation_id',
  'candidate_count',
  // Pangea memory lifecycle sweeps: aggregate counts, never record/member IDs.
  'speculative_expired',
  'stale_archived',
  'total_archived',
  'invalid_count',
  'stored_count',
  'truncated_turns',
  'agent_definition_id',
  // Webhook-tool delivery decision at dispatch (spec-declared): a boolean, a
  // bounded count, and a closed skip vocabulary — tool names/URLs never ride.
  'delivered',
  'tools_count',
  'skip_reason',
  // Analytics-history replay outcome (spec-declared): how many legacy rows one
  // member's replay read, wrote and dropped, how many query pages it took, and
  // the rows per page/chunk it used. Bounded counts, never an event name, a
  // property value or a member id.
  'events_read',
  'events_written',
  'events_dropped',
  'pages',
  'chunk_rows',
  // Voice-agent model override decision at dispatch (spec-declared): a boolean
  // plus two closed vocabularies (a logical model id and an effort level) from
  // server config — never a provider request, prompt or member content.
  'override_sent',
  'requested_llm_model',
  'requested_reasoning_effort',
  'screen_id',
  'screen_version',
  'failure_stage',
  // Check-in submit-failure attributes (spec-declared): the write surface/mode
  // classification and entry count — scalars, never member data.
  'mode',
  'entryCount',
  'errorKind',
  // Session lifecycle attributes (spec-declared, ADR-090). `reconcile_source`
  // and `intent_source` carry the information the spec called `source` — that
  // key is Datadog-reserved and stripped at the emit boundary, so a custom
  // attribute under it silently never arrives.
  'had_pending_intent',
  'session_generation',
  'reconcile_source',
  'intent_source',
  // Conversion attribution capture (ADR-132): where the campaign pair came
  // from — url or push. Same reserved-key story as `intent_source` above: the
  // spec's natural name is `source`, which Datadog strips.
  'attribution_source',
  // Teardown diagnostics (spec-declared, ADR-090): the registered participant
  // ids that failed to clear (a closed vocabulary, never member data) and
  // whether the credential clear itself succeeded — without these a device
  // stuck at `teardown-blocked` cannot be diagnosed from telemetry at all.
  'failed_participants',
  'credentials_cleared',
  // Bounded probe count on the launch reconcile — distinguishes cold-backend
  // warm-ups (attempts > 1) from single-shot successes.
  'attempts',
  // Empty-hydration warn attributes (spec-declared): the flow definition id, the
  // detection site (`origin` — a closed enum; the natural name `source` is
  // Datadog-reserved and stripped at the emit boundary), and the emptiness
  // classification — never answer values.
  'flow_id',
  'origin',
  'empty_kind',
  // Notification-sweeper run attributes (spec-declared): per-run cohort counts
  // and the deliverable-channel count — scalars, never tokens or message content.
  // `prunedDevices` counts device rows deleted after a dead-token ticket. It is
  // deliberately not named `prunedTokens`: allowlisting a key does not exempt it
  // from redaction, and `PHI_KEY_PATTERN` matches an unanchored `token`, so that
  // name emitted the counter as `[REDACTED]` — a scrubbed integer, observed in a
  // preview.
  'claimed',
  'sent',
  'failed',
  'deferred',
  'cancelled',
  'prunedDevices',
  'deliverableChannels',
  // Voice-sweeper unclosed-session WARN attributes (spec-declared): a capped
  // comma-joined sample of opaque session UUIDs (the debuggability channel the
  // metric deliberately omits — ids are allowed in logs, never metric tags,
  // ADR-069), the truncation flag, and the two sweep window knobs — scalars.
  'session_ids',
  // The agent definition of each listed session, aligned with `session_ids`;
  // admitted only as a bounded token list (see isSafeTokenList).
  'agent_definition_ids',
  'truncated',
  'grace_minutes',
  'lookback_hours',
  // Intents whose processing threw — kept distinct from `failed` (a provider
  // rejection, a normal outcome) so a defect cannot hide among dead tokens.
  'errored',
  // Notification dispatch dimensions (spec-declared): the notification
  // definition key and opaque intent UUID, the producer's gate/skip
  // classification (closed enums), the computed ISO fire time, whether the
  // natural-key insert created a row, a comma-joined set of provider error
  // CODES (never provider message text), and backfill cohort counts. Member
  // correlation rides `usr.id`, never an attribute.
  'definitionKey',
  // The reminder row's own id, on the check-in reminder producer's failure
  // event. A UUIDv7 surrogate key for a `reminders` row — creation-ordered and
  // opaque, carrying no member identity, no schedule time, and nothing drawn
  // from a check-in (the scan repo already treats it as non-member data). It is
  // what makes a produce failure attributable to a row rather than to the sweep
  // as a whole. Deliberately an ATTRIBUTE only, never a metric tag: one value
  // per reminder is unbounded cardinality for a dimension.
  'reminderId',
  // Which content-guard screen rejected a producer's generated copy — the
  // closed `COPY_GUARD_RULE` enum (clinical_label | lockscreen_term |
  // contact_detail | sona_title), never the copy it judged.
  'copy_guard_rule',
  'intentId',
  'gate',
  'skipped',
  'scheduledAt',
  'created',
  'providerErrors',
  // Vendor error class on a per-device notification send failure — a closed
  // token-shaped code from the push provider, never message content.
  'provider_error',
  'candidates',
  'enqueued',
  // The domain-event type behind a publish failure — a closed catalog value.
  'eventType',
  // Voice session connection fields — opaque room/session identifiers, never member content.
  'roomName',
  'sessionId',
  'sessionDurationMs',
  // WebRTC SDK disconnect context — classified into a closed vocabulary by the
  // telemetry layer before emission, never raw SDK free text.
  'contextType',
  'contextReason',
  'contextCode',
  'closeCode',
  'closeReason',
  // Provider-path attributes: bounded scalars that reach the vendor SDK through
  // the provider filter rather than the `assertSafeAttributes` emit boundary
  // (RUM/update lifecycle, generic enum-and-count dimensions). `status`,
  // `source`, `type`, and `timestamp` are Datadog-reserved as log attributes —
  // the telemetry emit boundary still flags them via DATADOG_RESERVED_LOG_KEYS;
  // their presence here only means they are PHI-safe scalars, not that they are
  // good custom log keys.
  'status',
  'source',
  'type',
  'code',
  'timestamp',
  'activityId',
  // Escalation log-event dimensions — the row's own opaque id and its closed
  // source vocabulary. The member id is deliberately NOT part of the escalation
  // log event: a member-linked clinical fact does not belong in operational
  // logs, and the linkage lives in the access-controlled row.
  'escalation_id',
  'escalation_source',
  'applied_at',
  'channel',
  'errorName',
  'errorType',
  'error_kind',
  // What threw, as bounded scalars beside the redacted free-text `detail`:
  // the thrown value's class and the `>`-joined class names of its cause
  // chain. Both are shape-guarded at the emit site — a class name is not
  // inherently bounded, since `name` is writable and a vendor SDK may derive
  // it from a response body.
  'error_class',
  'error_chain',
  // Where a Zod error thrown out of an API route was raised, as the
  // `<file>:<line>` of the first application stack frame: a code location
  // only, shape-guarded at the emit site.
  'throw_site',
  'event_name',
  'feature',
  'flowId',
  'hasAnnounceLabel',
  'hasVisibleSurface',
  'is_rollback',
  'itemCount',
  'itemTypeCount',
  'patchCount',
  'reload_to_confirm_ms',
  'reload_to_interactive_ms',
  // Notification-tap TTI: elapsed ms from a push tap to the destination
  // screen's first actionable state, plus the bounded cold/warm launch enum.
  'launch_kind',
  'launch_class',
  'tti_ms',
  'requestId',
  // Elapsed milliseconds since a Cognito pool user was created — a duration,
  // never an identifier. null when the age could not be established.
  'holderAgeMs',
  'runtime_version',
  'site',
  'toolCount',
  'trigger',
  // Join key for the migration handoff timing marks: a random UUIDv4 minted on
  // the device when a legacy exchange resolves and held in memory only. It
  // names one handoff attempt so its marks can be joined to each other; it is
  // never derived from the member, the device, or any credential.
  'handoff_attempt_id',
  // Which epoch clock a rejected migration readiness anchor came from ('launch' | 'intent'), and the phase it was measuring.
  'anchor',
  'operation_subtype',
  'update_id',
  // Per-call voice summary attributes (spec-declared): turn/word/tool counts,
  // provider-measured latency aggregates in seconds, and bounded provider
  // enums — numbers and closed vocabularies, never transcript content.
  // `binding_outcome` carries the member-binding result; the natural name
  // `member_binding` matches the redaction denylist's unanchored `member` and
  // would scrub to [REDACTED] at the emit boundary. `tool_names` is the
  // catalog-bounded tool identifier list, comma-joined.
  'binding_outcome',
  'agent_type',
  'turns_total',
  'turns_user',
  'turns_agent',
  'interrupted_turns',
  'user_words_avg',
  'user_words_max',
  'agent_words_avg',
  'agent_words_max',
  'response_latency_avg_s',
  'response_latency_p95_s',
  'response_latency_max_s',
  'llm_ttfb_avg_s',
  'llm_ttfb_p95_s',
  'tts_ttfb_avg_s',
  'tool_names',
  'tool_calls',
  'tool_errors',
  'termination_reason',
  'call_successful',
  'duration_s',
  // Journey-composer run telemetry (spec-declared): bounded per-run slot,
  // miss, and fallback counts, the code-hosted catalogue technique key, the
  // opaque batch UUID, the member-local YYYY-MM-DD day key, and a closed
  // failure-cause vocabulary (`reason` above already covers the trigger enum).
  // Enumerations, counts, and opaque ids only — never practice content, tile
  // copy, or member free text.
  'slots_requested',
  'slots_composed',
  'miss_count',
  'fallback_count',
  'technique_slug',
  // The per-call / per-launch technique dimension: the same code-hosted
  // catalogue key as `technique_slug`, under the property name the analytics
  // plane shares across both practice pathways.
  'technique',
  // Journey-launch lifecycle booleans on the `journey.practice.launched` twin
  // log (first-ever launch for the member; session reuse re-announcement).
  'first_ever',
  'reused',
  'batch_id',
  'cause',
  // The build-practice pipeline's closed refusal vocabulary (selection_timeout,
  // selector_refused, intent_too_thin, …), carried on a composer degrade so the
  // builder-refused bucket can be split without a database query, and on the
  // placement log beside the decision event the tool handler already emits. An
  // enum string from code, never model output or member content.
  'refusal_class',
  'day_key',
  // Where a first-arrival composition's tile copy came from (planned, authored,
  // mixed) — a closed enum from code, never the copy itself.
  'copy_source',
  // Which welcome series a first-arrival or carry-over composition used
  // (v1 | v2) — a closed enum from code.
  'welcome_series',
  // The journey planner's proposed technique on a builder-refused degrade — a
  // code-hosted catalogue key, the same vocabulary as `technique_slug` — and
  // which verification it went through (`code` | `llm`), a closed enum from
  // code. Neither is model output: the key is validated against the eligible
  // set before it is emitted, and the path is chosen by the pipeline.
  'proposed_technique',
  'verify_path',
  // How many of a run's default-technique candidates survived the journey's
  // intent dedupe. A count from code; the intents never ride along.
  'survivor_count',
  // Why the journey safety check left a slot empty: the closed veto-class
  // vocabulary (crisis, self_harm, withdrawal, medical, check_failed). The
  // model's words never ride with it — the class is the whole record.
  // (`trigger`, registered above, carries what put the member in scope.)
  'veto_class',
  // Per-source composition-context presence flags: whether each bounded input
  // section arrived non-empty for a run — booleans about section presence,
  // never the section content itself.
  'input_present_weekly_plan',
  'input_present_conversations',
  'input_present_check_ins',
  'input_present_memories',
  'input_present_intake',
  'input_present_today_plan',
  'input_present_offered_history',
  // Practice build placement (spec-declared): where in a conversation each
  // `build_practice` request happened, derived from transcript STRUCTURE —
  // turn counts, ordinals, a spine position, seconds into the call. Bounded
  // integers only; the transcript's words, the member's intent/anchor, and
  // any member or session id never ride these keys.
  'user_turns_before',
  'agent_turn_ordinal',
  'time_in_call_s',
  'build_ordinal',
  'message_position',
  // Legacy-source read seam (migration importer): the provider enum, requested
  // tier, the classified retry flag and bounded counts of what one read returned.
  'tier',
  'retry_safe',
  'table_count',
  'row_count',
  'bytes',
  // Legacy-source pooled connection health: whether the read replaced the connection, its idle age and the connect time (a flag and two ms counts).
  'reconnect',
  'idle_ms',
  'connect_ms',
  // Migration step replay (operator repair): the coded replay reason and
  // bounded counts of what one request resolved to and what it declined to
  // run, plus the Tier 1 row count that evidences a lost-publish re-emit.
  // Every one is a closed code or a small integer — never a step's data, a
  // stored reason's qualifier (which carries 1.0 identifiers), or a member id,
  // which rides in `correlation` and nowhere else.
  'replay_reason',
  'requested_step_count',
  'resolved_count',
  'dropped_not_in_manifest',
  'dropped_not_runnable',
  'dropped_human_class',
  'dropped_running',
  'dropped_already_done',
  'dropped_settled',
  'tier1_row_count_before',
  // The step owner that accepted the replay: one value of the manifest's closed
  // owner tuple, never member-derived.
  'runner',
  // A migration delivery acknowledged without retry because every failure was
  // human-class and recorded: how many steps failed, a small integer.
  'failed_step_count',
  // Access-grant audit-read attributes (ADR-069 Rule 2). This registry and the
  // metric-tag allowlist are SEPARATE, so admitting `filters`/`unfiltered` as
  // metric dimensions said nothing about them as log attributes — these are the
  // log half.
  //
  // `reader_internal_user_id` is a STAFF id, not a member one: the audit read is
  // a privileged act and who performed it is the point of recording it. The row
  // CONTENTS — member names, accessor emails, the free-text justification — are
  // deliberately absent and must stay so; re-exporting the audit's contents into
  // operational telemetry would defeat the thing being audited.
  'reader_internal_user_id',
  'reader_role',
  // Staff-provisioning ids (the member-import pre-pass that creates a
  // practitioner the importer has never seen): two opaque UUIDs naming the rows
  // it created. The practitioner's email, name and Healthie id are deliberately
  // not registered — the ids are how an operator finds the rows, and the rest
  // of the record stays in the database.
  'internal_user_id',
  'care_specialist_id',
  'filters',
  'unfiltered',
  'result_count',
  // Access-grant WRITE attributes, the counterpart to the read block above and
  // subject to the same boundary: the telemetry says that a justification was
  // recorded, by whom, and under which code — never what the justification
  // SAID. `reason_detail` is the free text a care person wrote about a member
  // and `reason_label` is the copy they were shown; neither is here, and
  // neither may be added. The member is absent for a stronger reason than
  // verbosity: this signal's population is non-assigned access to members, so a
  // member id in it would build a second, lower-governance copy of the
  // who-reached-whom mapping that `member_access_grants` and its manager-only
  // read surface exist to hold.
  //
  // `reason_code` appears in BOTH registries because they are independent: its
  // entry in the metric-tag allowlist says nothing about it as a log attribute,
  // and a key missing from either one is stripped silently at the emit
  // boundary.
  'accessor_internal_user_id',
  'reason_code',
  'grant_has_detail',
  // Client transport-failure diagnostics (spec-declared): whether the browser
  // let us read the upstream's diagnostic headers at all, the browser's own
  // connectivity reading, and the two opaque handles an upstream owner traces a
  // single request by. `aws_request_id` and `cf_cache` are vendor-assigned —
  // an API Gateway request id and CloudFront's bounded `x-cache` enum — and
  // carry no member identity, route interpolation, or free text.
  'trace_handles_readable',
  'browser_online',
  // Atlas staff Cognito sign-in step (password | mfa | new_password) — closed enum at emit site.
  'auth_step',
  'aws_request_id',
  'cf_cache',
  // Response-validation diagnostics (spec-declared): the fields a response
  // failed on. Atlas reports top-level field names; the API reports nested
  // `<path>:<zod issue code>` entries. Both draw only on the parsing schema's
  // own declared keys plus fixed sentinels — never a value, a record key, or an
  // issue message from the response. A Zod error thrown out of an API route
  // has no schema at hand, so there every object key is the `?` sentinel.
  'invalid_fields',
  // CM cutover run accounting (spec-declared). `env_key` is the deploy-target
  // key the run targets, `dry_run` a boolean, and the rest are row counts for
  // one operator-invoked run over an explicit member list. Counts and a
  // deploy-target key only — no member id, identity id, product, email or
  // delivery detail is admitted to this signal (ADR-069).
  'env_key',
  'dry_run',
  // Iterable device sync reconcile counts: how many devices one reconcile
  // registered and disabled. Integers only, never a token or user id.
  'registered',
  'disabled',
  'orders_inserted',
  'orders_skipped_conflict',
  'orders_unresolved_product',
  'in_flight_marked',
  // A publish that was lost or abandoned, reported as an event so the lost
  // envelope is nameable. `event_type` is the domain event's own type string,
  // authored in code and never derived from a payload; `address_kind` is the
  // same closed home/shipping vocabulary already registered as a metric tag,
  // and is domain-qualified for the reason that one is — a bare `kind` would
  // collide with unrelated producers on a registry shared estate-wide.
  //
  // Registered rather than left to the runtime's warn-and-pass: an
  // unregistered key survives today but is invisible to the one list ADR-069
  // Rule 2 makes the place to look, so the next reader cannot tell an
  // admitted key from one nobody has judged.
  'event_type',
  'address_kind', // Prescription-copy logs (spec-declared). `rx_sync_job` (sweep | reconcile |
  // backfill) and `issue_kind` (sweep_failed | reconcile_unrepaired |
  // unattributable) are closed vocabularies. `change_healthcare_id_ref` is the
  // id of a change_healthcare_ids row: an opaque UUID surrogate support looks
  // up in the database, never the CH person number, hsi value or member id.
  // It is a log attribute only; it is unbounded, so it is no metric tag.
  'rx_sync_job',
  'issue_kind',
  'change_healthcare_id_ref',
  // The Healthie form answer group id on the verified-test support events: the
  // opaque numeric id of one locked form, which support re-processes the form
  // by. It names no member and carries no answer, label or template. A log
  // attribute only; it is unbounded, so it is no metric tag.
  'healthie_form_answer_group_id',
  // Telemedicine call-push skip attribution (spec-declared): opaque appointment
  // id and device counts by row kind — scalars only, never tokens.
  'appointment_id',
  'devices_expo',
  'devices_expo_with_voip',
  'devices_voip_only',
  // Sweeper per-device failure log: whether the row was deleted after the send.
  'pruned',
  // The message of a DbQueryError, and nothing else: its constructor builds it
  // from the operation, a schema table name, the SQLSTATE, a constraint or
  // registered trigger name, the placeholder SQL and each parameter's type and
  // length, never a bound value. Any other error's message stays out.
  'db_query_error',
  // A failed Change Healthcare request's HTTP status and body size, both
  // integers. Its other dimensions are closed vocabularies in
  // ENUM_ATTRIBUTE_VALUES. Namespaced because a bare `status` is
  // Datadog-reserved, and none names a body, patient or prescription, which the
  // standard redactor scrubs. Log attributes only, never metric tags.
  'ch_http_status',
  'ch_response_bytes',
  // A summary completion that did not parse as JSON: offsets and lengths into
  // it, and booleans about its braces and fences. Its closed vocabularies are
  // in ENUM_ATTRIBUTE_VALUES. Never its text. Log attributes only.
  'summary_parse_error_position',
  'summary_parse_output_length',
  'summary_parse_candidate_length',
  'summary_parse_prefix_length',
  'summary_parse_suffix_length',
  'summary_parse_starts_with_brace',
  'summary_parse_ends_with_brace',
  'summary_parse_fence_present',
  'summary_parse_fence_stripped',
  // Healthie reconcile run summary, flat scalars only: whether the run was a dry
  // run, the window length in hours (never the window's instants), whether it
  // resumed or retained a stored checkpoint, and one count per item outcome.
  'dryRun',
  'windowHours',
  'resumedCheckpoint',
  'checkpointRetained',
  'processedCount',
  'skippedCount',
  'duplicateCount',
  'failedCount',
  'wouldConsumeCount',
  'wouldProcessCount',
  'unreadableTimestampCount',
  // Rows a page listed but the budget left unprocessed, kept for a retry.
  'deferredCount',
  // The run's retry-table pass: rows retried, resolved, gone from Healthie and
  // abandoned. For an abandoned row: its attempts and its age in hours (never
  // its id or instant).
  'retriedCount',
  'retryResolvedCount',
  'retryNotFoundCount',
  'retryAbandonedCount',
  'retryAttempts',
  'retryAgeHours',
  // A refused reward send: the opaque redemption and Sendoso product-variant
  // ids (never delivery data). How its body read went is the enum-bounded
  // `body_read` in ENUM_ATTRIBUTE_VALUES.
  'redemption_id',
  'product_variant_id',
]);

/** Keys admitted only as a bounded comma-joined list of id-shaped tokens. */
const TOKEN_LIST_KEYS = new Map<string, number>([['agent_definition_ids', 25]]);
const ID_TOKEN_RE = /^[a-z0-9][a-z0-9_.:-]{0,79}$/;

/** A comma-joined list of at most `maxTokens` id-shaped tokens; anything else is refused whole. */
function isSafeTokenList(value: unknown, maxTokens: number): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  const tokens = value.split(',');
  return tokens.length <= maxTokens && tokens.every((token) => ID_TOKEN_RE.test(token));
}

/**
 * Keep only registered top-level scalars. Unknown keys and nested values are
 * dropped, so a caller cannot export an arbitrary URL/path/free-text payload by
 * choosing an innocuous key that the PHI denylist does not recognize.
 */
export function filterSafeAttributes(
  attributes?: Record<string, unknown>,
): Record<string, string | number | boolean> | undefined {
  if (!attributes) return undefined;

  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (!SAFE_ATTR_KEYS.has(key)) continue;
    if (runtimeRules.has(key) && !isSafeRuntimeAttribute(key, value)) continue;
    if (runtimeAvailabilityKeys.has(key) && !isSafeRuntimeAttribute(key, value)) continue;
    const maxTokens = TOKEN_LIST_KEYS.get(key);
    if (maxTokens !== undefined && !isSafeTokenList(value, maxTokens)) continue;
    const allowed = enumAttributeValues.get(key);
    if (allowed && !(typeof value === 'string' && allowed.includes(value))) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      safe[key] = value;
    }
  }
  return safe;
}

/** An enum token — anything wider is free text and must not reach the vendor. */
const VENDOR_ENUM_TOKEN_RE = /^[A-Za-z0-9_.-]{1,40}$/;

/**
 * Bounds a value before it may be reported under the `vendor_enum_value` key.
 *
 * That key is admitted to the default-deny registry above only on the strength
 * of this check: the input is an unrecognised value read straight off a vendor
 * wire, so anything that is not already token-shaped is reduced to its type.
 */
export function boundVendorEnumValue(value: unknown): string {
  return typeof value === 'string' && VENDOR_ENUM_TOKEN_RE.test(value)
    ? value
    : `<${typeof value}>`;
}
